import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Checkbox,
  Select,
  Switch,
  Table,
  Upload,
  message,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  CalendarOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {
  analyzeAllPolicyDocuments,
  api,
  createEditablePolicyCopy,
  deletePolicyDocument,
  downloadCombinedPoliciesPdf,
  downloadPolicyAnalysisPdf,
  fetchPolicyInsuranceTypes,
  fetchRelevantTenderTemplates,
  reanalyzePolicyDocument,
  replacePolicyInsuranceTypes,
  selectTenderTemplateForPolicy,
  updateEditablePolicyCopy,
  type PolicyExportType,
  type RelevantTenderTemplatesResponse
} from './api';
import {
  createLeadTodo,
  fetchLeadTodoEvents,
  fetchLeadTodos,
  fetchTodoAssignees,
  updateLeadTodo,
  type LeadTodoPayload
} from './leadTodosApi';
import TenderWorkspace from './TenderWorkspace';
import NeedsAnalysisPanel from './NeedsAnalysisPanel';
import CustomerOfferPanel from './CustomerOfferPanel';
import {
  formatAmount,
  formatCvrNumber,
  statusLabelMap,
  statusTagColors,
  formatDateTimeShort,
  formatDateTimeFull,
  leadTierDefinitionMap,
  leadTierTagColors,
  deriveLeadTier,
  todoStatusLabels,
  todoStatusColors,
  todoPriorityLabels,
  todoPriorityColors
} from './constants';
import type {
  CompanyPropertyPortfolioResponse,
  CompanyInsuranceRelevanceEvents,
  CompanyPropertyRegisterChanges,
  CompanyPropertyRegisterLookup,
  CvrSummary,
  Label,
  LeadGroup,
  LeadGroupMember,
  LeadPolicyAnalysisEdit,
  LeadPolicyDocument,
  LeadTodo,
  LeadTodoEvent,
  PolicyEditablePayload,
  TodoAssignee
} from './types';
import { InsuranceType, LeadInsuranceType } from './types';
import {
  normalizeEditablePolicyPayload,
  toEditablePayloadFromDocument,
  type PolicyEditableArrayKey
} from './policyEditableUtils';
import {
  formatDateTime,
  leadEventTypeLabels,
  policyStatusColors,
  policyStatusLabels,
  toLocalInputValue
} from './leadDetailUtils';
import PolicyAnalysisActions from './PolicyAnalysisActions';
import PolicyAnalysisDetails from './PolicyAnalysisDetails';

const { Text, Paragraph } = Typography;

const AZURE_DI_CONFIG_ERROR_PATTERN = /Azure Document Intelligence er ikke konfigureret/i;
const MOTOR_READINESS_FIELDS = [
  'motor.vehicle_description',
  'motor.registration_number',
  'motor.usage',
  'motor.policy_number',
  'motor.liability_coverage',
  'motor.casco_coverage'
] as const;

const MOTOR_COVERAGE_KEYS = [
  'motor.liability_coverage',
  'motor.casco_coverage',
  'motor.driver_accident_coverage',
  'motor.legal_aid_coverage',
  'motor.loss_of_use_coverage'
] as const;

const MOTOR_COVERAGE_LABELS: Record<string, string> = {
  'motor.liability_coverage': 'Ansvar',
  'motor.casco_coverage': 'Kasko',
  'motor.driver_accident_coverage': 'Førerulykke',
  'motor.legal_aid_coverage': 'Retshjælp',
  'motor.loss_of_use_coverage': 'Afsavn'
};

const MOTOR_FIELD_LABELS: Record<string, string> = {
  'motor.vehicle_description': 'Køretøj',
  'motor.registration_number': 'Reg.nr.',
  'motor.usage': 'Anvendelse',
  'motor.policy_number': 'Policenr.',
  'motor.liability_coverage': 'Ansvar',
  'motor.casco_coverage': 'Kasko'
};

type PolicyScopeFilter = 'ALL' | 'MOTOR';
type PolicyStatusFilter = 'ALL' | 'PENDING' | 'COMPLETED' | 'FAILED';
type PolicyReadinessFilter = 'ALL' | 'READY' | 'MISSING';

const FINANCIAL_METRIC_EXPLANATIONS: Record<string, string> = {
  'Omsætning': 'Virksomhedens indtægter fra salg og ydelser før omkostninger.',
  'Bruttofortjeneste': 'Indtjening efter vareforbrug og andre direkte omkostninger, før personale- og administrationsomkostninger.',
  'Personaleomkostninger': 'Udgifter til løn, pension og øvrige medarbejderomkostninger.',
  'Af- og nedskrivninger': 'Regnskabsmæssigt værdifald på blandt andet maskiner, inventar og immaterielle aktiver.',
  'Driftsresultat': 'Resultat fra den almindelige drift før finansielle poster og skat.',
  'Resultat før skat': 'Årets resultat efter finansielle poster, men før skat.',
  'Årets resultat': 'Resultatet efter skat, som påvirker virksomhedens egenkapital.',
  'Aktiver': 'Den samlede værdi af virksomhedens ejendele og tilgodehavender.',
  'Egenkapital': 'Ejernes andel af virksomheden, når gæld og øvrige forpligtelser er trukket fra aktiverne.',
  'Langfristet gæld': 'Gæld med forfald senere end et år.',
  'Kortfristet gæld': 'Gæld og forpligtelser med forfald inden for et år.',
  'Samlede forpligtelser': 'Virksomhedens gæld eksklusive egenkapital.',
  'Likvider': 'Kontanter og indeståender, der umiddelbart kan bruges til betalinger.',
  'Omsætningsaktiver': 'Aktiver, der normalt omsættes eller realiseres inden for et år, fx likvider og tilgodehavender.',
  'Soliditetsgrad': 'Egenkapital divideret med aktiver. Viser hvor stor en del af virksomheden der er finansieret af egenkapital.',
  'Likviditetsgrad': 'Omsætningsaktiver divideret med kortfristet gæld. Viser evnen til at betale kortfristede forpligtelser.'
};

type FinancialMetric = {
  label: string;
  current: number | null;
  previous: number | null;
  calculated?: boolean;
};

type FinancialStatement = {
  status: 'AVAILABLE' | 'NO_XBRL';
  cvrNumber: string;
  companyName: string | null;
  reportingPeriod: { startDate: string | null; endDate: string | null } | null;
  publishedAt: string | null;
  caseNumber: string | null;
  sourceUrl: string | null;
  sourceFormat: 'XML' | null;
  auditType: string | null;
  fetchedAt: string;
  incomeStatement: FinancialMetric[];
  balanceSheet: FinancialMetric[];
  ratios: FinancialMetric[];
};

function formatFinancialValue(value: number | null, calculated = false): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Ikke oplyst i regnskabet';
  if (calculated) return new Intl.NumberFormat('da-DK', { style: 'percent', maximumFractionDigits: 1 }).format(value / 100);
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(value);
}

function formatFinancialChange(metric: FinancialMetric): string {
  if (metric.current === null || metric.previous === null || metric.previous === 0) return '—';
  const change = ((metric.current - metric.previous) / Math.abs(metric.previous)) * 100;
  return `${change >= 0 ? '+' : ''}${new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 }).format(change)} %`;
}

function formatPortfolioArea(value: number) {
  return `${new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(value)} m2`;
}

function extractWorkListLink(description: string) {
  const match = description.match(/(\/\?view=arbejdslister&workListId=[^\s]+)/i);
  return match ? match[1] : null;
}

function toPolicyUiErrorMessage(error: unknown, fallback: string): string {
  const rawMessage = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (AZURE_DI_CONFIG_ERROR_PATTERN.test(rawMessage)) {
    return 'Azure Document Intelligence mangler serverkonfiguration (endpoint + nøgle). Kontakt en administrator.';
  }
  return rawMessage || fallback;
}

type LeadEvent = {
  id: string;
  event_type: string;
  actor_user_id: string;
  from_status?: string | null;
  to_status?: string | null;
  created_at: string;
  notes?: string | null;
};

type LeadMeeting = {
  id: string;
  scheduled_for: string | null;
  location: string | null;
  summary: string | null;
  created_at: string;
};

type TodoFormValues = {
  title: string;
  description?: string;
  status: LeadTodo['status'];
  priority: LeadTodo['priority'];
  due_at?: Dayjs | null;
  assigned_to?: string | null;
  sync_to_calendar?: boolean;
};

type LeadDetailResponse = {
  lead: any;
  events: LeadEvent[];
  meetings: LeadMeeting[];
  labels?: Label[];
  cvrSummary?: CvrSummary | null;
};

type PolicyEditDraft = {
  summary: string;
  payload: PolicyEditablePayload;
};

type LeadSearchResult = {
  id: string;
  company_name: string;
  cvrnummer: string | null;
  status: string;
};

interface LeadDetailPageProps {
  canManage: boolean;
  onBack: (state?: { fromCompanySearch?: string | null }) => void;
  onLeadUpdated?: (leadId?: string | null) => void | Promise<void>;
  leadIdOverride?: string | null;
  locationStateOverride?: { fromCompanySearch?: string | null };
  hideInsuranceTypes?: boolean;
}

export default function LeadDetailPage({
  canManage,
  onBack,
  onLeadUpdated,
  leadIdOverride,
  locationStateOverride,
  hideInsuranceTypes = false
}: LeadDetailPageProps) {
  const params = useParams<{ leadId: string }>();
  const location = useLocation();
  const hideInsurance = !canManage;
  const effectiveLeadId = leadIdOverride ?? params.leadId ?? null;
  const leadId = effectiveLeadId;
  const backLocationState = useMemo(
    () => locationStateOverride ?? ((location.state as { fromCompanySearch?: string | null }) ?? undefined),
    [location.state, locationStateOverride]
  );
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState<any | null>(null);
  const [leadGroup, setLeadGroup] = useState<LeadGroup | null>(null);
  const [leadGroupLoading, setLeadGroupLoading] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalMode, setLinkModalMode] = useState<'create' | 'add'>('create');
  const [selectedLinkLeadId, setSelectedLinkLeadId] = useState<string | null>(null);
  const [linkModalSubmitting, setLinkModalSubmitting] = useState(false);
  const [leadSearchResults, setLeadSearchResults] = useState<LeadSearchResult[]>([]);
  const [leadSearchLoading, setLeadSearchLoading] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const leadSearchTimeoutRef = useRef<number | null>(null);
  const [removingGroupMemberId, setRemovingGroupMemberId] = useState<string | null>(null);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [meetings, setMeetings] = useState<LeadMeeting[]>([]);
  const [cvrSummary, setCvrSummary] = useState<CvrSummary | null>(null);
  const [initialCvrSummary, setInitialCvrSummary] = useState<CvrSummary | null>(null);
  const [cvrLookupLoading, setCvrLookupLoading] = useState(false);
  const [cvrLookupError, setCvrLookupError] = useState<string | null>(null);
  const [propertyPortfolio, setPropertyPortfolio] = useState<CompanyPropertyPortfolioResponse['portfolio']>(null);
  const [propertyPortfolioLoading, setPropertyPortfolioLoading] = useState(false);
  const [propertyPortfolioError, setPropertyPortfolioError] = useState<string | null>(null);
  const [propertyDetail, setPropertyDetail] = useState<CompanyPropertyRegisterLookup | null>(null);
  const [propertyDetailLoading, setPropertyDetailLoading] = useState(false);
  const [propertyDetailError, setPropertyDetailError] = useState<string | null>(null);
  const [propertyChanges, setPropertyChanges] = useState<CompanyPropertyRegisterChanges | null>(null);
  const [propertyChangesError, setPropertyChangesError] = useState<string | null>(null);
  const [insuranceEvents, setInsuranceEvents] = useState<CompanyInsuranceRelevanceEvents | null>(null);
  const [insuranceEventsError, setInsuranceEventsError] = useState<string | null>(null);
  const [financialStatement, setFinancialStatement] = useState<FinancialStatement | null>(null);
  const [financialStatementLoading, setFinancialStatementLoading] = useState(false);
  const [financialStatementError, setFinancialStatementError] = useState<string | null>(null);
  const cvrLookupSeq = useRef(0);
  const propertyPortfolioLookupSeq = useRef(0);
  const propertyDetailLookupSeq = useRef(0);
  const [notesForm] = Form.useForm();
  const [meetingForm] = Form.useForm();
  const [todoForm] = Form.useForm();
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingMeeting, setSavingMeeting] = useState(false);
  const [savingTodo, setSavingTodo] = useState(false);
  const [leadLabels, setLeadLabels] = useState<Label[]>([]);
  const [allLabels, setAllLabels] = useState<Label[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);
  const [removingLabelId, setRemovingLabelId] = useState<string | null>(null);
  const [callAttemptLoading, setCallAttemptLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [deletingMeetingId, setDeletingMeetingId] = useState<string | null>(null);
  const [editingMeeting, setEditingMeeting] = useState<LeadMeeting | null>(null);
  const [meetingDrawerOpen, setMeetingDrawerOpen] = useState(false);
  const [todoDrawerOpen, setTodoDrawerOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<LeadTodo | null>(null);
  const [todos, setTodos] = useState<LeadTodo[]>([]);
  const [todosLoading, setTodosLoading] = useState(false);
  const [todoHistoryModalOpen, setTodoHistoryModalOpen] = useState(false);
  const [todoHistoryLoading, setTodoHistoryLoading] = useState(false);
  const [todoHistory, setTodoHistory] = useState<LeadTodoEvent[]>([]);
  const [historyTodo, setHistoryTodo] = useState<LeadTodo | null>(null);
  const [assignees, setAssignees] = useState<TodoAssignee[]>([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [availableInsuranceTypes, setAvailableInsuranceTypes] = useState<InsuranceType[]>([]);
  const [insuranceTypesLoading, setInsuranceTypesLoading] = useState(false);
  const [policyInsuranceTypesById, setPolicyInsuranceTypesById] = useState<Record<string, LeadInsuranceType[]>>({});
  const [policyInsuranceLoadingById, setPolicyInsuranceLoadingById] = useState<Record<string, boolean>>({});
  const [policyTemplatesById, setPolicyTemplatesById] = useState<Record<string, RelevantTenderTemplatesResponse>>({});
  const [policyTemplateLoadingById, setPolicyTemplateLoadingById] = useState<Record<string, boolean>>({});
  const [policyTemplateSavingById, setPolicyTemplateSavingById] = useState<Record<string, boolean>>({});
  const [policyInsuranceModalOpen, setPolicyInsuranceModalOpen] = useState(false);
  const [policyInsuranceModalSaving, setPolicyInsuranceModalSaving] = useState(false);
  const [policyInsuranceModalPolicyId, setPolicyInsuranceModalPolicyId] = useState<string | null>(null);
  const [selectedPolicyInsuranceTypeIds, setSelectedPolicyInsuranceTypeIds] = useState<string[]>([]);
  const [removingPolicyInsuranceKey, setRemovingPolicyInsuranceKey] = useState<string | null>(null);
  const [policyDocuments, setPolicyDocuments] = useState<LeadPolicyDocument[]>([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyUploading, setPolicyUploading] = useState(false);
  const [policyFileList, setPolicyFileList] = useState<UploadFile[]>([]);
  const [policyEditDrafts, setPolicyEditDrafts] = useState<Record<string, PolicyEditDraft>>({});
  const [policySavingById, setPolicySavingById] = useState<Record<string, boolean>>({});
  const [policyCopyCreatingById, setPolicyCopyCreatingById] = useState<Record<string, boolean>>({});
  const [policyReanalyzingById, setPolicyReanalyzingById] = useState<Record<string, boolean>>({});
  const [policyAnalyzeAllLoading, setPolicyAnalyzeAllLoading] = useState(false);
  const [policyAnalyzeAllBackground, setPolicyAnalyzeAllBackground] = useState(false);
  const [policyAnalyzeAllSawPending, setPolicyAnalyzeAllSawPending] = useState(false);
  const [analysisMessageApi, analysisMessageContextHolder] = message.useMessage();
  const [combinedPolicyExportType, setCombinedPolicyExportType] = useState<PolicyExportType>('tender');
  const [combinedPolicyExportLoading, setCombinedPolicyExportLoading] = useState(false);
  const [prepareTenderWorkspacesLoading, setPrepareTenderWorkspacesLoading] = useState(false);
  const [selectedPolicyDocumentId, setSelectedPolicyDocumentId] = useState<string | null>(null);
  const [selectedTenderPolicyId, setSelectedTenderPolicyId] = useState<string | null>(null);
  const [policySearchTerm, setPolicySearchTerm] = useState('');
  const [policyScopeFilter, setPolicyScopeFilter] = useState<PolicyScopeFilter>('MOTOR');
  const [policyStatusFilter, setPolicyStatusFilter] = useState<PolicyStatusFilter>('ALL');
  const [policyReadinessFilter, setPolicyReadinessFilter] = useState<PolicyReadinessFilter>('ALL');
  const policyAnalyzeAllStartedAtRef = useRef<number | null>(null);
  const watchedCvrValue = Form.useWatch('cvrnummer', notesForm);
  const watchedTodoDue = Form.useWatch('due_at', todoForm);
  const savedLeadCvr = lead?.cvrnummer || '';
  const callAttemptCount = Number(lead?.call_attempt_count ?? 0);
  const isHidden = Boolean(lead?.hidden_at);
  const lastCallShort = formatDateTimeShort(lead?.last_call_at ?? null);
  const lastCallFull = formatDateTimeFull(lead?.last_call_at ?? null);

  useEffect(() => {
    if (!watchedTodoDue) {
      todoForm.setFieldsValue({ sync_to_calendar: false });
    }
  }, [watchedTodoDue, todoForm]);

  const commissionPercentRaw = lead?.commission_percent;
  const commissionPercent =
    commissionPercentRaw === undefined || commissionPercentRaw === null
      ? null
      : Number(commissionPercentRaw);
  const contractValueRaw = lead?.contract_value_annual;
  const contractValue =
    contractValueRaw === undefined || contractValueRaw === null ? null : Number(contractValueRaw);
  const commissionValue =
    commissionPercent !== null && contractValue !== null && !Number.isNaN(contractValue)
      ? (contractValue * commissionPercent) / 100
      : null;
  const commissionDisplay =
    commissionPercent === null || Number.isNaN(commissionPercent)
      ? '-'
      : `${commissionPercent}%${
          commissionValue !== null && !Number.isNaN(commissionValue)
            ? ` (${formatAmount(commissionValue, lead?.currency)})`
            : ''
        }`;
  const employeesRaw = lead?.cvr_ansatte_aar ?? lead?.ansatte_aar;
  const employeesCount = employeesRaw === undefined || employeesRaw === null ? null : Number(employeesRaw);
  const employeesDisplay =
    employeesCount === null || Number.isNaN(employeesCount) ? employeesRaw ?? null : employeesCount;
  const employeesDateRaw = lead?.cvr_ansatte_datotil ?? lead?.ansatte_datotil;
  const employeesYear = (() => {
    if (!employeesDateRaw) return null;
    const parsed = new Date(employeesDateRaw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.getFullYear().toString();
    }
    const yearCandidate = String(employeesDateRaw).slice(0, 4);
    return yearCandidate.length === 4 ? yearCandidate : String(employeesDateRaw);
  })();
  const formattedCvr = formatCvrNumber(lead?.cvrnummer) || '';
  const leadTier = deriveLeadTier(lead?.status);
  const tierMeta = leadTierDefinitionMap.get(leadTier);
  const tierLabel =
    tierMeta?.label ??
    (leadTier === 'CUSTOMER'
      ? 'Kunde'
      : leadTier === 'IN_PROGRESS'
        ? 'Sag'
        : 'Lead');
  const tierDescription = tierMeta?.description;
  const tierTagColor = leadTierTagColors[leadTier] || 'default';

  const cvrAddress = useMemo(() => {
    if (!cvrSummary) {
      return '';
    }
    const segments: string[] = [];
    const streetParts: string[] = [];
    if (cvrSummary.vejnavn) {
      streetParts.push(cvrSummary.vejnavn.trim());
    }
    const from = cvrSummary.husnummer_fra;
    const to = cvrSummary.husnummer_til;
    let houseNumber = '';
    if (from && to && to !== from) {
      houseNumber = `${from}-${to}`;
    } else if (from) {
      houseNumber = from;
    } else if (to) {
      houseNumber = to;
    }
    if (houseNumber) {
      streetParts.push(houseNumber);
    }
    const detailParts: string[] = [];
    if (cvrSummary.etage) {
      detailParts.push(`${cvrSummary.etage}.`);
    }
    if (cvrSummary.doer) {
      detailParts.push(cvrSummary.doer);
    }
    if (detailParts.length) {
      streetParts.push(detailParts.join(' '));
    }
    if (streetParts.length) {
      segments.push(streetParts.join(' ').replace(/\s+/g, ' ').trim());
    }
    if (cvrSummary.supplerende_bynavn) {
      segments.push(cvrSummary.supplerende_bynavn);
    }
    const postalParts: string[] = [];
    if (cvrSummary.postnummer) {
      postalParts.push(cvrSummary.postnummer);
    }
    if (cvrSummary.postdistrikt) {
      postalParts.push(cvrSummary.postdistrikt);
    }
    if (postalParts.length) {
      segments.push(postalParts.join(' '));
    }
    if (cvrSummary.kommunenavn) {
      segments.push(cvrSummary.kommunenavn);
    }
    return segments.join(', ');
  }, [cvrSummary]);

  const sortedLeadLabels = useMemo(
    () => [...leadLabels].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' })),
    [leadLabels]
  );

  const unassignedLabels = useMemo(
    () => allLabels.filter((label) => !leadLabels.some((assigned) => assigned.id === label.id)),
    [allLabels, leadLabels]
  );

  const completedPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status === 'COMPLETED').length,
    [policyDocuments]
  );

  const notCompletedPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status !== 'COMPLETED').length,
    [policyDocuments]
  );

  const pendingPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status === 'PENDING').length,
    [policyDocuments]
  );

  const failedPolicyCount = useMemo(
    () => policyDocuments.filter((doc) => doc.analysis_status === 'FAILED').length,
    [policyDocuments]
  );

  const policyEffectivePayload = useCallback((doc: LeadPolicyDocument): Record<string, unknown> => {
    const source = (doc.analysis_edit?.payload || doc.effective_analysis_payload || doc.analysis_payload) as unknown;
    return source && typeof source === 'object' && !Array.isArray(source)
      ? source as Record<string, unknown>
      : {};
  }, []);

  const policyExcelFields = useCallback((doc: LeadPolicyDocument): Array<Record<string, unknown>> => {
    const payload = policyEffectivePayload(doc);
    const fields = payload.excel_fields;
    if (!Array.isArray(fields)) {
      return [];
    }
    return fields.filter((item) => item && typeof item === 'object') as Array<Record<string, unknown>>;
  }, [policyEffectivePayload]);

  const policyFieldValue = useCallback((doc: LeadPolicyDocument, key: string): string => {
    const field = policyExcelFields(doc).find((item) => String(item.field_key || '').trim() === key);
    const value = field?.value;
    return typeof value === 'string' ? value.trim() : value === undefined || value === null ? '' : String(value);
  }, [policyExcelFields]);

  const isMotorPolicy = useCallback((doc: LeadPolicyDocument): boolean => {
    const fromInsuranceType = (policyInsuranceTypesById[doc.id] || []).some((item) =>
      `${item.title || ''} ${item.category || ''}`.toLowerCase().includes('motor')
    );
    if (fromInsuranceType) {
      return true;
    }
    return policyExcelFields(doc).some((item) => String(item.field_key || '').startsWith('motor.'));
  }, [policyExcelFields, policyInsuranceTypesById]);

  const policyMotorReadiness = useCallback((doc: LeadPolicyDocument) => {
    if (!isMotorPolicy(doc)) {
      return null;
    }
    const missingKeys = MOTOR_READINESS_FIELDS.filter((key) => !policyFieldValue(doc, key));
    return {
      missingKeys,
      isReady: missingKeys.length === 0,
      completionLabel: `${MOTOR_READINESS_FIELDS.length - missingKeys.length}/${MOTOR_READINESS_FIELDS.length}`
    };
  }, [isMotorPolicy, policyFieldValue]);

  const policyCoverageTags = useCallback((doc: LeadPolicyDocument): string[] => {
    if (!isMotorPolicy(doc)) {
      return [];
    }
    return MOTOR_COVERAGE_KEYS.filter((key) => {
      const value = policyFieldValue(doc, key).toLowerCase();
      return value === 'ja' || value === 'yes' || value === 'true';
    }).map((key) => MOTOR_COVERAGE_LABELS[key]);
  }, [isMotorPolicy, policyFieldValue]);

  const filteredPolicyDocuments = useMemo(() => {
    const search = policySearchTerm.trim().toLowerCase();
    return policyDocuments.filter((doc) => {
      const motor = isMotorPolicy(doc);
      if (policyScopeFilter === 'MOTOR' && !motor) {
        return false;
      }
      if (policyStatusFilter !== 'ALL' && doc.analysis_status !== policyStatusFilter) {
        return false;
      }
      const readiness = policyMotorReadiness(doc);
      if (policyReadinessFilter === 'READY' && readiness && !readiness.isReady) {
        return false;
      }
      if (policyReadinessFilter === 'MISSING' && (!readiness || readiness.isReady)) {
        return false;
      }
      if (!search) {
        return true;
      }
      const haystack = [
        doc.file_name,
        policyFieldValue(doc, 'motor.registration_number'),
        policyFieldValue(doc, 'motor.vehicle_description'),
        policyFieldValue(doc, 'motor.policy_number')
      ].join(' ').toLowerCase();
      return haystack.includes(search);
    });
  }, [
    isMotorPolicy,
    policyDocuments,
    policyFieldValue,
    policyMotorReadiness,
    policyReadinessFilter,
    policyScopeFilter,
    policySearchTerm,
    policyStatusFilter
  ]);

  useEffect(() => {
    if (!policyDocuments.length) {
      setSelectedPolicyDocumentId(null);
      return;
    }
    if (!selectedPolicyDocumentId || !policyDocuments.some((doc) => doc.id === selectedPolicyDocumentId)) {
      setSelectedPolicyDocumentId(policyDocuments[0].id);
    }
  }, [policyDocuments, selectedPolicyDocumentId]);

  useEffect(() => {
    if (!filteredPolicyDocuments.length) {
      return;
    }
    if (!selectedPolicyDocumentId || !filteredPolicyDocuments.some((doc) => doc.id === selectedPolicyDocumentId)) {
      setSelectedPolicyDocumentId(filteredPolicyDocuments[0].id);
    }
  }, [filteredPolicyDocuments, selectedPolicyDocumentId]);

  const selectedPolicyDocument = useMemo(
    () => policyDocuments.find((doc) => doc.id === selectedPolicyDocumentId) || null,
    [policyDocuments, selectedPolicyDocumentId]
  );

  const tenderWorkspacePolicies = useMemo(
    () => policyDocuments.filter((document) => Boolean(policyTemplatesById[document.id]?.selectedTemplateId)),
    [policyDocuments, policyTemplatesById]
  );

  const selectedTenderPolicy = useMemo(
    () => tenderWorkspacePolicies.find((document) => document.id === selectedTenderPolicyId) || null,
    [selectedTenderPolicyId, tenderWorkspacePolicies]
  );

  const loadFinancialStatement = useCallback(async () => {
    if (!leadId) return;
    setFinancialStatementLoading(true);
    setFinancialStatementError(null);
    try {
      const response = await api(`/api/leads/${leadId}/financial-statement/refresh`, { method: 'POST' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || 'Kunne ikke hente regnskabsdata fra Virk');
      }
      setFinancialStatement((result as { statement?: FinancialStatement | null }).statement || null);
    } catch (error: any) {
      setFinancialStatementError(error?.message || 'Kunne ikke hente regnskabsdata fra Virk');
      setFinancialStatement(null);
    } finally {
      setFinancialStatementLoading(false);
    }
  }, [leadId]);

  const loadSavedFinancialStatement = useCallback(async () => {
    if (!leadId) return;
    try {
      const response = await api(`/api/leads/${leadId}/financial-statement`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.error || 'Kunne ikke hente det gemte regnskab');
      setFinancialStatement((result as { statement?: FinancialStatement | null }).statement || null);
    } catch (error: any) {
      setFinancialStatementError(error?.message || 'Kunne ikke hente det gemte regnskab');
    }
  }, [leadId]);

  const tenderAmbiguousPolicies = useMemo(() => (
    policyDocuments
      .map((document) => ({ document, context: policyTemplatesById[document.id] || null }))
      .filter(({ context }) => Boolean(context) && !context?.selectedTemplateId && (context?.templates.length || 0) > 1)
  ), [policyDocuments, policyTemplatesById]);

  useEffect(() => {
    if (!tenderWorkspacePolicies.length) {
      setSelectedTenderPolicyId(null);
      return;
    }
    if (!selectedTenderPolicyId || !tenderWorkspacePolicies.some((document) => document.id === selectedTenderPolicyId)) {
      setSelectedTenderPolicyId(tenderWorkspacePolicies[0].id);
    }
  }, [selectedTenderPolicyId, tenderWorkspacePolicies]);

  const labelOptions = useMemo(
    () =>
      unassignedLabels.map((label) => ({
        value: label.id,
        label: label.hideInSearch ? `${label.name} (Skjul i søgeresultat)` : label.name
      })),
    [unassignedLabels]
  );

  const validateOptionalCvr = useCallback((_: unknown, value: string | undefined) => {
    if (value === undefined || value === null || value === '') {
      return Promise.resolve();
    }
    const digits = `${value}`.replace(/\D+/g, '');
    if (!digits) {
      return Promise.resolve();
    }
    if (digits.length === 8) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('CVR-nummer skal bestå af 8 cifre'));
  }, []);

  const loadInsuranceTypes = useCallback(async () => {
    if (hideInsurance || !canManage) {
      setAvailableInsuranceTypes([]);
      return;
    }
    setInsuranceTypesLoading(true);
    try {
      const res = await api('/api/insurance-types');
      const data = (await res.json().catch(() => [])) as InsuranceType[];
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente forsikringstyper');
      }
      const normalized = Array.isArray(data) ? data : [];
      setAvailableInsuranceTypes(
        [...normalized].sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }))
      );
    } catch (error: any) {
      console.error('Failed to load insurance types', error);
      message.error(error?.message || 'Kunne ikke hente forsikringstyper');
    } finally {
      setInsuranceTypesLoading(false);
    }
  }, [canManage, hideInsurance]);

  const normalizePolicyInsuranceItems = useCallback((items: LeadInsuranceType[]) => {
    const normalized = Array.isArray(items)
      ? items.map((item) => ({
          ...item,
          insuranceTypeId: String(item.insuranceTypeId ?? (item as any)?.insurancetypeid ?? ''),
          title: item.title ?? '',
          category: item.category ?? '',
          description: item.description ?? null
        }))
      : [];

    return normalized.sort((a, b) => a.title.localeCompare(b.title, 'da', { sensitivity: 'base' }));
  }, []);

  const loadPolicyInsuranceForDocuments = useCallback(async (documents: LeadPolicyDocument[]) => {
    if (hideInsurance || !leadId || !canManage || documents.length === 0) {
      setPolicyInsuranceTypesById({});
      return;
    }

    const loadingEntries = documents.reduce<Record<string, boolean>>((acc, doc) => {
      acc[doc.id] = true;
      return acc;
    }, {});
    setPolicyInsuranceLoadingById(loadingEntries);

    try {
      const entries = await Promise.all(
        documents.map(async (doc) => {
          const payload = await fetchPolicyInsuranceTypes(leadId, doc.id);
          return [doc.id, normalizePolicyInsuranceItems(payload.items || [])] as const;
        })
      );

      setPolicyInsuranceTypesById(Object.fromEntries(entries));
    } catch (error: any) {
      console.error('Failed to load policy insurance types', error);
      message.error(error?.message || 'Kunne ikke hente forsikringstyper for policer');
      setPolicyInsuranceTypesById({});
    } finally {
      setPolicyInsuranceLoadingById({});
    }
  }, [canManage, hideInsurance, leadId, normalizePolicyInsuranceItems]);

  const loadPolicyTemplatesForDocuments = useCallback(async (documents: LeadPolicyDocument[]) => {
    if (!leadId || documents.length === 0) {
      setPolicyTemplatesById({});
      setPolicyTemplateLoadingById({});
      return;
    }

    setPolicyTemplateLoadingById(documents.reduce<Record<string, boolean>>((current, document) => {
      current[document.id] = true;
      return current;
    }, {}));
    const entries = await Promise.all(documents.map(async (document) => {
      try {
        return [document.id, await fetchRelevantTenderTemplates(leadId, document.id)] as const;
      } catch (_error) {
        return [document.id, null] as const;
      }
    }));
    setPolicyTemplatesById(Object.fromEntries(entries.filter((entry): entry is readonly [string, RelevantTenderTemplatesResponse] => entry[1] !== null)));
    setPolicyTemplateLoadingById({});
  }, [leadId]);

  useEffect(() => {
    if (hideInsurance) {
      return;
    }
    void loadInsuranceTypes();
  }, [hideInsurance, loadInsuranceTypes]);

  const loadLeadPolicies = useCallback(async () => {
    if (!leadId) {
      setPolicyDocuments([]);
      setPolicyEditDrafts({});
      setPolicyInsuranceTypesById({});
      setPolicyInsuranceLoadingById({});
      return;
    }
    setPolicyLoading(true);
    try {
      const res = await api(`/api/leads/${leadId}/policies`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente policer');
      }
      const docs = Array.isArray((data as any)?.documents) ? (data as any).documents : [];
      setPolicyDocuments(docs as LeadPolicyDocument[]);
      setPolicyEditDrafts({});
      await Promise.all([
        loadPolicyInsuranceForDocuments(docs as LeadPolicyDocument[]),
        loadPolicyTemplatesForDocuments(docs as LeadPolicyDocument[])
      ]);
    } catch (error: any) {
      console.error('Failed to load lead policies', error);
      message.error(error?.message || 'Kunne ikke hente policer');
      setPolicyInsuranceTypesById({});
      setPolicyInsuranceLoadingById({});
    } finally {
      setPolicyLoading(false);
    }
  }, [leadId, loadPolicyInsuranceForDocuments, loadPolicyTemplatesForDocuments]);

  const loadTodos = useCallback(async () => {
    if (!leadId || !canManage) {
      setTodos([]);
      return;
    }
    try {
      setTodosLoading(true);
      const data = await fetchLeadTodos(leadId);
      setTodos(data);
    } catch (error: any) {
      console.error('Failed to load lead todos', error);
      message.error(error?.message || 'Kunne ikke hente opgaver');
    } finally {
      setTodosLoading(false);
    }
  }, [canManage, leadId]);

  const loadTodoAssignees = useCallback(async () => {
    if (!canManage) {
      setAssignees([]);
      return;
    }
    try {
      setAssigneesLoading(true);
      const data = await fetchTodoAssignees();
      setAssignees(data);
    } catch (error: any) {
      console.error('Failed to load todo assignees', error);
      message.error(error?.message || 'Kunne ikke hente brugere');
    } finally {
      setAssigneesLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    void loadLeadPolicies();
  }, [loadLeadPolicies]);

  useEffect(() => {
    void loadSavedFinancialStatement();
  }, [loadSavedFinancialStatement]);

  useEffect(() => {
    if (!policyAnalyzeAllBackground || !leadId) {
      return;
    }

    const timer = window.setInterval(() => {
      void loadLeadPolicies();
    }, 5000);

    return () => {
      window.clearInterval(timer);
    };
  }, [leadId, loadLeadPolicies, policyAnalyzeAllBackground]);

  useEffect(() => {
    if (!policyAnalyzeAllBackground) {
      setPolicyAnalyzeAllSawPending(false);
      return;
    }

    if (pendingPolicyCount > 0) {
      if (!policyAnalyzeAllSawPending) {
        setPolicyAnalyzeAllSawPending(true);
      }
      return;
    }

    const startedAt = policyAnalyzeAllStartedAtRef.current;
    const elapsed = startedAt ? Date.now() - startedAt : 0;
    const waitLongEnoughWithoutPending = !policyAnalyzeAllSawPending && elapsed >= 15000;

    if (!policyAnalyzeAllSawPending && !waitLongEnoughWithoutPending) {
      return;
    }

    setPolicyAnalyzeAllBackground(false);
    policyAnalyzeAllStartedAtRef.current = null;

    if (failedPolicyCount > 0) {
      analysisMessageApi.warning(`AI-analyse afsluttet. ${failedPolicyCount} dokumenter kræver manuel gennemgang.`);
    } else {
      analysisMessageApi.success('AI-analyse afsluttet. Alle dokumenter er opdateret.');
    }
  }, [analysisMessageApi, failedPolicyCount, pendingPolicyCount, policyAnalyzeAllBackground, policyAnalyzeAllSawPending]);

  const handlePolicyUpload = useCallback(async () => {
    if (!leadId || policyFileList.length === 0) {
      return;
    }
    setPolicyUploading(true);
    try {
      const formData = new FormData();
      policyFileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });
      const res = await api(`/api/leads/${leadId}/policies`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke uploade policer');
      }
      message.success('Policer uploadet');
      setPolicyFileList([]);
      await loadLeadPolicies();
      
      // Start analysis automatically after successful upload
      try {
        const result = await analyzeAllPolicyDocuments(leadId, false, true);
        if (result.accepted) {
          policyAnalyzeAllStartedAtRef.current = Date.now();
          setPolicyAnalyzeAllSawPending(false);
          setPolicyAnalyzeAllBackground(true);
          analysisMessageApi.info('AI-analyse kører i baggrunden. Du får besked her, når den er færdig.');
        }
      } catch (analyzeError) {
        console.warn('Failed to start auto-analysis', analyzeError);
      }
    } catch (error: any) {
      console.error('Failed to upload lead policies', error);
      message.error(toPolicyUiErrorMessage(error, 'Kunne ikke uploade policer'));
    } finally {
      setPolicyUploading(false);
    }
  }, [leadId, loadLeadPolicies, policyFileList, analysisMessageApi]);

  const handleDeletePolicy = useCallback((doc: LeadPolicyDocument) => {
    if (!leadId) {
      return;
    }

    Modal.confirm({
      title: 'Slet police?',
      content: `Er du sikker på, at du vil slette ${doc.file_name || 'dette dokument'}?`,
      okText: 'Slet',
      okButtonProps: { danger: true },
      cancelText: 'Annuller',
      onOk: async () => {
        try {
          await deletePolicyDocument(leadId, doc.id);
          message.success('Policedokument slettet');
          await loadLeadPolicies();
        } catch (error: any) {
          console.error('Failed to delete policy document', error);
          message.error(error?.message || 'Kunne ikke slette policedokument');
        }
      }
    });
  }, [leadId, loadLeadPolicies]);

  const handlePolicyTemplateChange = useCallback(async (policyId: string, templateId: string) => {
    if (!leadId) return;
    setPolicyTemplateSavingById((current) => ({ ...current, [policyId]: true }));
    try {
      await selectTenderTemplateForPolicy(leadId, policyId, templateId);
      setPolicyTemplatesById((current) => ({
        ...current,
        [policyId]: current[policyId] ? { ...current[policyId], selectedTemplateId: templateId } : current[policyId]
      }));
      message.success('Udbudsskabelon valgt for policen');
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke vælge udbudsskabelon');
    } finally {
      setPolicyTemplateSavingById((current) => ({ ...current, [policyId]: false }));
    }
  }, [leadId]);

  const handlePrepareAllTenderWorkspaces = useCallback(async () => {
    if (!leadId || policyDocuments.length === 0) {
      return;
    }

    setPrepareTenderWorkspacesLoading(true);
    let autoSelected = 0;
    let alreadySelected = 0;
    let ambiguous = 0;
    let missingInsuranceTypes = 0;
    let noTemplates = 0;
    let failed = 0;
    let autoLinkedMotorInsuranceTypes = 0;
    const templateUpdates: Array<[string, RelevantTenderTemplatesResponse]> = [];
    const insuranceTypeUpdates: Array<[string, LeadInsuranceType[]]> = [];

    try {
      for (const document of policyDocuments) {
        let templateContext = policyTemplatesById[document.id] || null;
        if (!templateContext) {
          try {
            templateContext = await fetchRelevantTenderTemplates(leadId, document.id);
            templateUpdates.push([document.id, templateContext]);
          } catch (_error) {
            failed += 1;
            continue;
          }
        }

        if (templateContext.selectedTemplateId) {
          alreadySelected += 1;
          continue;
        }

        if (!templateContext.policyInsuranceTypes.length) {
          const liabilityValue = policyFieldValue(document, 'motor.liability_coverage').toLowerCase();
          const motorLiabilityType = availableInsuranceTypes.find((type) =>
            /motor.*ansvar|ansvar.*motor/i.test(`${type.title} ${type.category}`)
          );
          const canInferMotorLiability = isMotorPolicy(document)
            && ['ja', 'yes', 'true'].includes(liabilityValue)
            && Boolean(motorLiabilityType);

          if (canInferMotorLiability && motorLiabilityType) {
            try {
              const insuranceTypesResponse = await replacePolicyInsuranceTypes(
                leadId,
                document.id,
                [motorLiabilityType.id]
              );
              const insuranceTypes = insuranceTypesResponse.items || [];
              templateContext = await fetchRelevantTenderTemplates(leadId, document.id);
              insuranceTypeUpdates.push([document.id, insuranceTypes]);
              templateUpdates.push([document.id, templateContext]);
              autoLinkedMotorInsuranceTypes += 1;
            } catch (_error) {
              failed += 1;
              continue;
            }
          }
        }

        if (!templateContext.policyInsuranceTypes.length) {
          missingInsuranceTypes += 1;
          continue;
        }

        if (templateContext.templates.length === 0) {
          noTemplates += 1;
          continue;
        }

        if (templateContext.templates.length > 1) {
          ambiguous += 1;
          continue;
        }

        const templateId = templateContext.templates[0]?.id;
        if (!templateId) {
          ambiguous += 1;
          continue;
        }

        try {
          await selectTenderTemplateForPolicy(leadId, document.id, templateId);
          templateContext = { ...templateContext, selectedTemplateId: templateId };
          templateUpdates.push([document.id, templateContext]);
          autoSelected += 1;
        } catch (_error) {
          failed += 1;
        }
      }

      if (templateUpdates.length > 0) {
        setPolicyTemplatesById((current) => ({
          ...current,
          ...Object.fromEntries(templateUpdates)
        }));
      }
      if (insuranceTypeUpdates.length > 0) {
        setPolicyInsuranceTypesById((current) => ({
          ...current,
          ...Object.fromEntries(insuranceTypeUpdates)
        }));
      }

      const summaryParts: string[] = [];
      if (autoSelected > 0) summaryParts.push(`auto-valgt: ${autoSelected}`);
      if (alreadySelected > 0) summaryParts.push(`allerede valgt: ${alreadySelected}`);
      if (autoLinkedMotorInsuranceTypes > 0) summaryParts.push(`motoransvar koblet: ${autoLinkedMotorInsuranceTypes}`);
      if (ambiguous > 0) summaryParts.push(`flere kandidater: ${ambiguous}`);
      if (missingInsuranceTypes > 0) summaryParts.push(`mangler forsikringstyper: ${missingInsuranceTypes}`);
      if (noTemplates > 0) summaryParts.push(`ingen matchende skabelon: ${noTemplates}`);
      if (failed > 0) summaryParts.push(`fejl: ${failed}`);

      if (autoSelected > 0) {
        message.success(`Klargøring gennemført (${summaryParts.join(' · ')})`);
      } else {
        message.info(summaryParts.length ? `Ingen auto-valg (${summaryParts.join(' · ')})` : 'Ingen ændringer');
      }
    } finally {
      setPrepareTenderWorkspacesLoading(false);
    }
  }, [leadId, policyDocuments, policyTemplatesById]);

  const handlePolicyDownload = useCallback(async (doc: LeadPolicyDocument) => {
    try {
      const res = await api(doc.download_url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any)?.error || 'Kunne ikke hente dokumentet');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name || 'policy-document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download policy', error);
      message.error(error?.message || 'Kunne ikke hente dokumentet');
    }
  }, []);

  const handlePolicyViewOriginal = useCallback(async (doc: LeadPolicyDocument) => {
    const targetUrl = typeof doc.view_url === 'string' && doc.view_url.trim() ? doc.view_url : doc.download_url;
    const previewWindow = window.open('', '_blank', 'noopener,noreferrer');

    try {
      const res = await api(targetUrl);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any)?.error || 'Kunne ikke åbne original policedokument');
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60_000);
    } catch (error: any) {
      if (previewWindow) {
        previewWindow.close();
      }
      console.error('Failed to view original policy', error);
      message.error(error?.message || 'Kunne ikke åbne original policedokument');
    }
  }, []);

  const handlePolicyPdfExport = useCallback(
    async (doc: LeadPolicyDocument, type: PolicyExportType) => {
      if (!leadId) {
        return;
      }

      try {
        const blob = await downloadPolicyAnalysisPdf(leadId, doc.id, type);
        const url = window.URL.createObjectURL(blob);
        const baseName = (doc.file_name || 'policy-document').replace(/\.[^.]+$/, '');
        const suffix = type === 'tender' ? 'udbudsmateriale' : 'aftaledokument';
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseName}-${suffix}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        console.error('Failed to export policy PDF', error);
        message.error(error?.message || 'Kunne ikke generere PDF');
      }
    },
    [leadId]
  );

  const handleCombinedPolicyPdfExport = useCallback(async () => {
    if (!leadId || completedPolicyCount === 0) {
      return;
    }

    setCombinedPolicyExportLoading(true);
    try {
      const blob = await downloadCombinedPoliciesPdf(leadId, combinedPolicyExportType);
      const url = window.URL.createObjectURL(blob);
      const companyBaseName = (lead?.company_name || 'lead').replace(/[^a-zA-Z0-9._-]+/g, '_');
      const suffix = combinedPolicyExportType === 'tender' ? 'udbudsmateriale' : 'aftaledokument';
      const link = document.createElement('a');
      link.href = url;
      link.download = `${companyBaseName}-samlet-${suffix}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export combined policy PDF', error);
      message.error(error?.message || 'Kunne ikke generere samlet PDF');
    } finally {
      setCombinedPolicyExportLoading(false);
    }
  }, [combinedPolicyExportType, completedPolicyCount, lead?.company_name, leadId]);

  const handleAnalyzeAllPolicies = useCallback(async () => {
    if (!leadId || policyDocuments.length === 0) {
      return;
    }

    let includeCompleted = false;
    let modalRef: ReturnType<typeof Modal.confirm> | null = null;

    const buildModalContent = (checked: boolean) => {
      const targetCount = checked ? policyDocuments.length : notCompletedPolicyCount;
      return (
        <Space direction="vertical" size={8}>
          <Text>
            Der analyseres {targetCount} dokumenter.
            {checked
              ? ' Allerede analyserede dokumenter analyseres også igen.'
              : ' Allerede analyserede dokumenter springes over.'}
          </Text>
          <Checkbox
            checked={checked}
            onChange={(event) => {
              includeCompleted = event.target.checked;
              modalRef?.update({
                title: includeCompleted ? 'AI-analysér alle uploadede policer?' : 'AI-analysér alle manglende policer?',
                content: buildModalContent(includeCompleted)
              });
            }}
          >
            Analysér også dokumenter der allerede er analyseret
          </Checkbox>
        </Space>
      );
    };

    modalRef = Modal.confirm({
      title: 'AI-analysér alle manglende policer?',
      content: buildModalContent(includeCompleted),
      okText: 'Start analyse',
      cancelText: 'Annuller',
      onOk: async () => {
        setPolicyAnalyzeAllLoading(true);
        try {
          const result = await analyzeAllPolicyDocuments(leadId, includeCompleted, true);

          if (result.accepted) {
            policyAnalyzeAllStartedAtRef.current = Date.now();
            setPolicyAnalyzeAllSawPending(false);
            setPolicyAnalyzeAllBackground(true);
            await loadLeadPolicies();

            if (result.analyzed_documents === 0) {
              setPolicyAnalyzeAllBackground(false);
              analysisMessageApi.info('Alle uploadede policer var allerede analyseret');
              return;
            }

            analysisMessageApi.info('AI-analyse kører i baggrunden. Du får besked her, når den er færdig.');
            return;
          }

          await loadLeadPolicies();

          if (result.analyzed_documents === 0) {
            message.info('Alle uploadede policer var allerede analyseret');
            return;
          }

          message.success(
            `AI-analyse færdig: ${result.analyzed_completed} gennemført, ${result.analyzed_failed} fejlede, ${result.skipped_completed} spranget over`
          );
        } catch (error: any) {
          console.error('Failed to analyze all policies', error);
          message.error(toPolicyUiErrorMessage(error, 'Kunne ikke analysere alle policer'));
        } finally {
          setPolicyAnalyzeAllLoading(false);
        }
      }
    });
  }, [analysisMessageApi, leadId, loadLeadPolicies, notCompletedPolicyCount, policyDocuments.length]);

  const updatePolicyDocument = useCallback((policyId: string, updater: (doc: LeadPolicyDocument) => LeadPolicyDocument) => {
    setPolicyDocuments((prev) => prev.map((doc) => (doc.id === policyId ? updater(doc) : doc)));
  }, []);

  const beginPolicyEdit = useCallback((doc: LeadPolicyDocument, edit?: LeadPolicyAnalysisEdit | null) => {
    const sourceEdit = edit ?? doc.analysis_edit ?? null;
    const payload = sourceEdit?.payload ? normalizeEditablePolicyPayload(sourceEdit.payload) : toEditablePayloadFromDocument(doc);
    const summary = sourceEdit?.summary ?? doc.analysis_summary ?? '';
    setPolicyEditDrafts((prev) => ({
      ...prev,
      [doc.id]: {
        summary,
        payload
      }
    }));
  }, []);

  const handleCreatePolicyCopy = useCallback(async (doc: LeadPolicyDocument) => {
    if (!leadId) return;
    setPolicyCopyCreatingById((prev) => ({ ...prev, [doc.id]: true }));
    try {
      const data = await createEditablePolicyCopy(leadId, doc.id);
      if (!data.edit) {
        throw new Error('Kopi kunne ikke oprettes');
      }

      updatePolicyDocument(doc.id, (currentDoc) => ({
        ...currentDoc,
        analysis_edit: data.edit,
        effective_analysis_payload: (data.effective_analysis_payload || currentDoc.effective_analysis_payload || currentDoc.analysis_payload) as any,
        effective_analysis_summary: data.effective_analysis_summary ?? currentDoc.effective_analysis_summary ?? currentDoc.analysis_summary
      }));
      beginPolicyEdit(doc, data.edit);
      message.success('Redigerbar kopi oprettet');
    } catch (error: any) {
      console.error('Failed to create editable policy copy', error);
      message.error(error?.message || 'Kunne ikke oprette redigerbar kopi');
    } finally {
      setPolicyCopyCreatingById((prev) => {
        const next = { ...prev };
        delete next[doc.id];
        return next;
      });
    }
  }, [beginPolicyEdit, leadId, updatePolicyDocument]);

  const handleStartPolicyEdit = useCallback((doc: LeadPolicyDocument) => {
    beginPolicyEdit(doc, doc.analysis_edit ?? null);
  }, [beginPolicyEdit]);

  const handleCancelPolicyEdit = useCallback((policyId: string) => {
    setPolicyEditDrafts((prev) => {
      const next = { ...prev };
      delete next[policyId];
      return next;
    });
  }, []);

  const updatePolicyDraftSummary = useCallback((policyId: string, value: string) => {
    setPolicyEditDrafts((prev) => {
      const draft = prev[policyId];
      if (!draft) return prev;
      return {
        ...prev,
        [policyId]: {
          ...draft,
          summary: value
        }
      };
    });
  }, []);

  const updatePolicyDraftLine = useCallback(
    (policyId: string, section: PolicyEditableArrayKey, index: number, field: string, value: unknown) => {
      setPolicyEditDrafts((prev) => {
        const draft = prev[policyId];
        if (!draft) return prev;
        const source = Array.isArray((draft.payload as any)[section]) ? ([...(draft.payload as any)[section]] as Array<Record<string, unknown>>) : [];
        if (!source[index]) return prev;
        source[index] = {
          ...source[index],
          [field]: value
        };
        return {
          ...prev,
          [policyId]: {
            ...draft,
            payload: {
              ...draft.payload,
              [section]: source
            }
          }
        };
      });
    },
    []
  );

  const handleSavePolicyEdit = useCallback(async (doc: LeadPolicyDocument) => {
    if (!leadId) return;
    const draft = policyEditDrafts[doc.id];
    if (!draft) return;

    setPolicySavingById((prev) => ({ ...prev, [doc.id]: true }));
    try {
      const data = await updateEditablePolicyCopy(leadId, doc.id, {
        summary: draft.summary?.trim() ? draft.summary.trim() : null,
        payload: draft.payload
      });

      if (!data.edit) {
        throw new Error('Kunne ikke gemme redigerbar kopi');
      }

      updatePolicyDocument(doc.id, (currentDoc) => ({
        ...currentDoc,
        analysis_edit: data.edit,
        effective_analysis_payload: (data.effective_analysis_payload || currentDoc.effective_analysis_payload || currentDoc.analysis_payload) as any,
        effective_analysis_summary: data.effective_analysis_summary ?? currentDoc.effective_analysis_summary ?? currentDoc.analysis_summary
      }));
      handleCancelPolicyEdit(doc.id);
      message.success('AI-kopi gemt');
    } catch (error: any) {
      console.error('Failed to save editable policy copy', error);
      message.error(error?.message || 'Kunne ikke gemme redigering');
    } finally {
      setPolicySavingById((prev) => {
        const next = { ...prev };
        delete next[doc.id];
        return next;
      });
    }
  }, [handleCancelPolicyEdit, leadId, policyEditDrafts, updatePolicyDocument]);

  const handleReanalyzePolicy = useCallback((doc: LeadPolicyDocument) => {
    if (!leadId) return;

    Modal.confirm({
      title: 'Kør ny AI-analyse?',
      content: 'Hvis der findes en redigerbar kopi, bruges den som grundlag for ny analyse.',
      okText: 'Kør ny analyse',
      cancelText: 'Annuller',
      okButtonProps: { danger: true },
      onOk: async () => {
        setPolicyReanalyzingById((prev) => ({ ...prev, [doc.id]: true }));
        try {
          await reanalyzePolicyDocument(leadId, doc.id);
          await loadLeadPolicies();
          message.success('Ny AI-analyse er gennemført');
        } catch (error: any) {
          console.error('Failed to reanalyze policy document', error);
          message.error(toPolicyUiErrorMessage(error, 'Kunne ikke køre ny AI analyse'));
        } finally {
          setPolicyReanalyzingById((prev) => {
            const next = { ...prev };
            delete next[doc.id];
            return next;
          });
        }
      }
    });
  }, [leadId, loadLeadPolicies]);

  const formatPolicyFileSize = useCallback((size?: number | null) => {
    if (!size) return 'Ukendt størrelse';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const formatPolicyAnalysisError = useCallback((rawError?: string | null) => {
    const errorText = typeof rawError === 'string' ? rawError.trim() : '';
    if (!errorText) {
      return 'Ukendt fejl under AI-analyse.';
    }

    if (/Policy analysis validation failed/i.test(errorText) || /kvalitetssikres automatisk/i.test(errorText)) {
      return 'AI-analysen blev oprettet, men svaret var for ufuldstændigt til sikker visning. Prøv "Ny AI-analyse" igen.';
    }

    return errorText;
  }, []);

  const cleanPolicySnippet = useCallback((rawValue: unknown) => {
    if (typeof rawValue !== 'string') {
      return '';
    }

    return rawValue
      .replace(/[\u201C\u201D\u00AB\u00BB"]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/([A-Za-zÆØÅæøå])(?=\d)/g, '$1 ')
      .replace(/\)(?=\d)/g, ') · ')
      .replace(/(\d{1,3}(?:\.\d{3})*,\d{2})(?=\d{1,3}(?:\.\d{3})*,\d{2})/g, '$1 · ')
      .replace(/\s*·\s*/g, ' · ')
      .trim();
  }, []);

  const formatCoverageNotes = useCallback((rawValue: unknown) => cleanPolicySnippet(rawValue), [cleanPolicySnippet]);
  const formatCoverageEvidence = useCallback((rawValue: unknown) => cleanPolicySnippet(rawValue), [cleanPolicySnippet]);

  const getPolicyStatusLabel = useCallback((doc: LeadPolicyDocument) => {
    if (doc.analysis_status === 'PENDING') {
      const isUnderAnalysis = Boolean(policyReanalyzingById[doc.id]) || policyAnalyzeAllBackground;
      return isUnderAnalysis ? 'Under analyse' : 'Uploaded';
    }

    return policyStatusLabels[doc.analysis_status] || doc.analysis_status;
  }, [policyAnalyzeAllBackground, policyReanalyzingById]);

  const renderPolicyAnalysisActions = useCallback((doc: LeadPolicyDocument) => (
    <PolicyAnalysisActions
      doc={doc}
      isEditing={Boolean(policyEditDrafts[doc.id])}
      policyAnalyzeAllBackground={policyAnalyzeAllBackground}
      policyCopyCreating={Boolean(policyCopyCreatingById[doc.id])}
      policySaving={Boolean(policySavingById[doc.id])}
      policyReanalyzing={Boolean(policyReanalyzingById[doc.id])}
      onCreateCopy={handleCreatePolicyCopy}
      onStartEdit={handleStartPolicyEdit}
      onSaveEdit={handleSavePolicyEdit}
      onCancelEdit={handleCancelPolicyEdit}
      onPdfExport={handlePolicyPdfExport}
      onReanalyze={handleReanalyzePolicy}
    />
  ), [
    handleCancelPolicyEdit,
    handleCreatePolicyCopy,
    handlePolicyPdfExport,
    handleReanalyzePolicy,
    handleSavePolicyEdit,
    handleStartPolicyEdit,
    policyAnalyzeAllBackground,
    policyCopyCreatingById,
    policyEditDrafts,
    policyReanalyzingById,
    policySavingById
  ]);

  const handleCreatePolicyFollowUp = useCallback(
    async (title: string, description: string) => {
      if (!leadId) {
        return;
      }

      try {
        const todo = await createLeadTodo(leadId, {
          title,
          description,
          status: 'NEW',
          priority: 'MEDIUM',
          due_at: null,
          assigned_to: null,
          sync_to_calendar: false
        });
        setTodos((prev) => [todo, ...prev]);
        message.success('Opgave oprettet');
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette opgave');
      }
    },
    [leadId]
  );

  const handleCreatePolicyFollowUpBatch = useCallback(
    async (items: Array<{ title: string; description: string }>) => {
      if (!leadId || !items.length) {
        return;
      }

      try {
        for (const item of items) {
          const todo = await createLeadTodo(leadId, {
            title: item.title,
            description: item.description,
            status: 'NEW',
            priority: 'MEDIUM',
            due_at: null,
            assigned_to: null,
            sync_to_calendar: false
          });
          setTodos((prev) => [todo, ...prev]);
        }
        message.success(`Oprettede ${items.length} opgaver`);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke oprette opgaver');
      }
    },
    [leadId]
  );

  const renderPolicyAnalysis = useCallback((doc: LeadPolicyDocument) => (
    <PolicyAnalysisDetails
      doc={doc}
      draft={policyEditDrafts[doc.id]}
      formatCoverageEvidence={formatCoverageEvidence}
      formatCoverageNotes={formatCoverageNotes}
      formatPolicyAnalysisError={formatPolicyAnalysisError}
      onCreateFollowUp={handleCreatePolicyFollowUp}
      onCreateFollowUpBatch={handleCreatePolicyFollowUpBatch}
      onUpdateDraftLine={updatePolicyDraftLine}
      onUpdateDraftSummary={updatePolicyDraftSummary}
    />
  ), [
    handleCreatePolicyFollowUp,
    handleCreatePolicyFollowUpBatch,
    formatCoverageEvidence,
    formatCoverageNotes,
    formatPolicyAnalysisError,
    policyEditDrafts,
    updatePolicyDraftLine,
    updatePolicyDraftSummary
  ]);

  const loadDetail = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      setCvrSummary(null);
      setPropertyPortfolio(null);
      setPropertyPortfolioError(null);
      setPropertyPortfolioLoading(false);
      setPropertyDetail(null);
      setPropertyDetailError(null);
      setPropertyChanges(null);
      setPropertyChangesError(null);
      setInsuranceEvents(null);
      setInsuranceEventsError(null);
      setPropertyDetailLoading(false);
      const res = await api(`/api/leads/${leadId}/detail`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke hente lead');
      }
      const data = (await res.json()) as LeadDetailResponse;
      setLead(data.lead);
      setEvents(data.events || []);
      setMeetings(data.meetings || []);
      setLeadLabels(
        Array.isArray(data.labels)
          ? [...data.labels].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
          : []
      );
      const summary = data.cvrSummary ?? null;
      const cvr = String(data.lead?.cvrnummer || '').replace(/\D+/g, '');
      setCvrSummary(summary);
      setInitialCvrSummary(summary);
      setCvrLookupError(null);
      setCvrLookupLoading(false);
      notesForm.setFieldsValue({
        notes: data.lead?.notes || '',
        cvrnummer: data.lead?.cvrnummer || '',
        contact_name: data.lead?.contact_name || '',
        contact_email: data.lead?.contact_email || '',
        contact_phone: data.lead?.contact_phone || ''
      });
      if (cvr.length === 8) {
        const lookupId = ++propertyPortfolioLookupSeq.current;
        setPropertyPortfolioLoading(true);
        void api(`/api/companies/${cvr}/property-portfolio`)
          .then(async (response) => {
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
              throw new Error((payload as any)?.error || 'Kunne ikke hente ejendomsportefølje');
            }
            if (lookupId === propertyPortfolioLookupSeq.current) {
              setPropertyPortfolio((payload as CompanyPropertyPortfolioResponse).portfolio || null);
              setPropertyPortfolioError(null);
            }
          })
          .catch((error: any) => {
            if (lookupId === propertyPortfolioLookupSeq.current) {
              setPropertyPortfolio(null);
              setPropertyPortfolioError(error?.message || 'Kunne ikke hente ejendomsportefølje');
            }
          })
          .finally(() => {
            if (lookupId === propertyPortfolioLookupSeq.current) {
              setPropertyPortfolioLoading(false);
            }
          });

        const propertyLookupId = ++propertyDetailLookupSeq.current;
        setPropertyDetailLoading(true);
        void Promise.all([
          api(`/api/companies/${cvr}/property-register`),
          api(`/api/companies/${cvr}/property-register/changes?limit=25`),
          api(`/api/companies/${cvr}/property-register/insurance-events?limit=25`)
        ])
          .then(async ([detailResponse, changesResponse, insuranceResponse]) => {
            const [detailPayload, changesPayload, insurancePayload] = await Promise.all([
              detailResponse.json().catch(() => ({})),
              changesResponse.json().catch(() => ({})),
              insuranceResponse.json().catch(() => ({}))
            ]);
            if (propertyLookupId !== propertyDetailLookupSeq.current) {
              return;
            }
            if (!detailResponse.ok) {
              throw new Error((detailPayload as any)?.error || 'Kunne ikke hente DAR/BBR-data');
            }
            setPropertyDetail(detailPayload as CompanyPropertyRegisterLookup);
            setPropertyDetailError(null);
            if (changesResponse.ok) {
              setPropertyChanges(changesPayload as CompanyPropertyRegisterChanges);
              setPropertyChangesError(null);
            } else {
              setPropertyChanges(null);
              setPropertyChangesError((changesPayload as any)?.error || 'Kunne ikke hente registerændringer');
            }
            if (insuranceResponse.ok) {
              setInsuranceEvents(insurancePayload as CompanyInsuranceRelevanceEvents);
              setInsuranceEventsError(null);
            } else {
              setInsuranceEvents(null);
              setInsuranceEventsError((insurancePayload as any)?.error || 'Kunne ikke hente forsikringssignaler');
            }
          })
          .catch((error: any) => {
            if (propertyLookupId === propertyDetailLookupSeq.current) {
              setPropertyDetail(null);
              setPropertyDetailError(error?.message || 'Kunne ikke hente DAR/BBR-data');
            }
          })
          .finally(() => {
            if (propertyLookupId === propertyDetailLookupSeq.current) {
              setPropertyDetailLoading(false);
            }
          });
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente lead');
      onBack();
    } finally {
      setLoading(false);
    }
  }, [leadId, notesForm, onBack]);

  const loadLeadGroup = useCallback(async (groupId: string | null) => {
    if (!groupId) {
      setLeadGroup(null);
      return;
    }
    try {
      setLeadGroupLoading(true);
      const res = await api(`/api/lead-groups/${groupId}`);
      if (res.status === 404) {
        setLeadGroup(null);
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as any)?.error || 'Kunne ikke hente samlekunde');
      }
      setLeadGroup({
        ...(data.group as LeadGroup),
        members: Array.isArray(data.members) ? (data.members as LeadGroupMember[]) : []
      });
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente samlekunde');
    } finally {
      setLeadGroupLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (lead?.lead_group_id) {
      void loadLeadGroup(lead.lead_group_id as string);
    } else {
      setLeadGroup(null);
    }
  }, [lead?.lead_group_id, loadLeadGroup]);

  useEffect(() => {
    void loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    void loadTodoAssignees();
  }, [loadTodoAssignees]);

  useEffect(() => {
    return () => {
      if (leadSearchTimeoutRef.current) {
        window.clearTimeout(leadSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!canManage) {
      setAllLabels([]);
      setSelectedLabelIds([]);
      setLabelsLoading(false);
      return;
    }

    let aborted = false;
    setLabelsLoading(true);

    (async () => {
      try {
        const res = await api('/api/labels');
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke hente labels');
        }
        const data = (await res.json()) as Label[];
        if (aborted) {
          return;
        }
        setAllLabels(
          [...data].sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }))
        );
      } catch (error: any) {
        if (!aborted) {
          console.error('Failed to load labels', error);
          message.error(error?.message || 'Kunne ikke hente labels');
        }
      } finally {
        if (!aborted) {
          setLabelsLoading(false);
        }
      }
    })();

    return () => {
      aborted = true;
    };
  }, [canManage]);

  useEffect(() => {
    if (!canManage) {
      return;
    }

    const raw = typeof watchedCvrValue === 'string' ? watchedCvrValue : '';
    const digits = raw.replace(/\D+/g, '');
    if (!digits) {
      setCvrLookupLoading(false);
      setCvrLookupError(null);
      setCvrSummary(initialCvrSummary);
      notesForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    if (digits.length !== 8) {
      const errMsg = 'CVR-nummer skal bestå af 8 cifre';
      setCvrLookupLoading(false);
      setCvrSummary(null);
      setCvrLookupError(errMsg);
      notesForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
      return;
    }

    if (digits === savedLeadCvr && initialCvrSummary) {
      setCvrSummary(initialCvrSummary);
      setCvrLookupLoading(false);
      setCvrLookupError(null);
      notesForm.setFields([{ name: 'cvrnummer', errors: [] }]);
      return;
    }

    notesForm.setFields([{ name: 'cvrnummer', errors: [] }]);
    const lookupId = ++cvrLookupSeq.current;
    setCvrLookupLoading(true);
    setCvrLookupError(null);

    (async () => {
      try {
        const res = await api(`/api/leads/lookup/cvr/${digits}`);
        if (lookupId !== cvrLookupSeq.current) {
          return;
        }

        if (!res.ok) {
          let errMsg = 'Kunne ikke slå CVR op';
          if (res.status === 404) {
            errMsg = 'Ingen virksomhed fundet for CVR-nummeret';
          } else if (res.status === 400) {
            errMsg = 'CVR-nummer skal bestå af 8 cifre';
          }
          setCvrSummary(null);
          setCvrLookupError(errMsg);
          notesForm.setFields([{ name: 'cvrnummer', errors: [errMsg] }]);
          return;
        }

        const data = (await res.json()) as CvrSummary;
        if (lookupId !== cvrLookupSeq.current) {
          return;
        }

        setCvrSummary(data);
        setCvrLookupError(null);
      } catch (error) {
        if (lookupId !== cvrLookupSeq.current) {
          return;
        }
        console.error('CVR lookup failed', error);
        const errMsg = 'Kunne ikke slå CVR op';
        setCvrSummary(null);
        setCvrLookupError(errMsg);
        message.error(errMsg);
      } finally {
        if (lookupId === cvrLookupSeq.current) {
          setCvrLookupLoading(false);
        }
      }
    })();
  }, [canManage, watchedCvrValue, notesForm, initialCvrSummary, savedLeadCvr]);

  const handleSaveNotes = async () => {
    if (!leadId) return;
    try {
      const values = await notesForm.validateFields();
      setSavingNotes(true);
      const sanitizedCvr = typeof values.cvrnummer === 'string' ? values.cvrnummer.replace(/\D+/g, '') : '';
      if (sanitizedCvr && sanitizedCvr.length !== 8) {
        message.error('CVR-nummer skal bestå af 8 cifre');
        setSavingNotes(false);
        return;
      }
      const contactName = typeof values.contact_name === 'string' ? values.contact_name.trim() : '';
      const contactEmailRaw = typeof values.contact_email === 'string' ? values.contact_email.trim() : '';
      const contactPhone = typeof values.contact_phone === 'string' ? values.contact_phone.trim() : '';
      let contactEmail: string | null = null;
      if (contactEmailRaw) {
        const lower = contactEmailRaw.toLowerCase();
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(lower)) {
          message.error('Ugyldig e-mailadresse');
          setSavingNotes(false);
          return;
        }
        contactEmail = lower;
      }
      const res = await api(`/api/leads/${leadId}/detail`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: values.notes ?? '',
          cvrnummer: sanitizedCvr ? sanitizedCvr : null,
          contact_name: contactName || null,
          contact_email: contactEmailRaw ? contactEmail : null,
          contact_phone: contactPhone || null
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere noter');
      }
      await res.json().catch(() => null);
      message.success('Detaljer opdateret');
      await loadDetail();
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Kunne ikke opdatere noter');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleOpenPolicyInsuranceModal = useCallback((policyId: string) => {
    const currentItems = policyInsuranceTypesById[policyId] || [];
    setPolicyInsuranceModalPolicyId(policyId);
    setSelectedPolicyInsuranceTypeIds(currentItems.map((item) => String(item.insuranceTypeId)));
    setPolicyInsuranceModalOpen(true);
  }, [policyInsuranceTypesById]);

  const handleClosePolicyInsuranceModal = useCallback(() => {
    setPolicyInsuranceModalOpen(false);
    setPolicyInsuranceModalPolicyId(null);
    setSelectedPolicyInsuranceTypeIds([]);
  }, []);

  const handleTogglePolicyInsuranceType = useCallback((id: string, checked: boolean) => {
    setSelectedPolicyInsuranceTypeIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return Array.from(next);
    });
  }, []);

  const handleSavePolicyInsuranceTypes = useCallback(async () => {
    if (!leadId || !canManage || !policyInsuranceModalPolicyId) {
      return;
    }
    setPolicyInsuranceModalSaving(true);
    try {
      const data = await replacePolicyInsuranceTypes(leadId, policyInsuranceModalPolicyId, selectedPolicyInsuranceTypeIds);
      const normalized = normalizePolicyInsuranceItems(data.items || []);
      setPolicyInsuranceTypesById((prev) => ({
        ...prev,
        [policyInsuranceModalPolicyId]: normalized
      }));

      message.success('Forsikringstyper opdateret');
      if (data.editableCopyCreated) {
        message.success('Redigerbar kopi blev automatisk oprettet for policen');
      }
      if (data.editableCopyCreated || data.editableCopyUpdated) {
        void loadLeadPolicies();
      }
      handleClosePolicyInsuranceModal();
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke opdatere forsikringstyper');
    } finally {
      setPolicyInsuranceModalSaving(false);
    }
  }, [
    canManage,
    handleClosePolicyInsuranceModal,
    leadId,
    loadLeadPolicies,
    normalizePolicyInsuranceItems,
    onLeadUpdated,
    policyInsuranceModalPolicyId,
    selectedPolicyInsuranceTypeIds
  ]);

  const handleRemovePolicyInsuranceType = useCallback(async (policyId: string, insuranceTypeId: string) => {
    if (!leadId || !canManage) {
      return;
    }
    const key = `${policyId}:${insuranceTypeId}`;
    setRemovingPolicyInsuranceKey(key);
    try {
      const currentItems = policyInsuranceTypesById[policyId] || [];
      const remainingIds = currentItems
        .map((item) => String(item.insuranceTypeId))
        .filter((id) => id !== insuranceTypeId);
      const data = await replacePolicyInsuranceTypes(leadId, policyId, remainingIds);
      const normalized = normalizePolicyInsuranceItems(data.items || []);
      setPolicyInsuranceTypesById((prev) => ({
        ...prev,
        [policyId]: normalized
      }));
      message.success('Forsikringstype fjernet');
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke fjerne forsikringstype');
    } finally {
      setRemovingPolicyInsuranceKey(null);
    }
  }, [canManage, leadId, normalizePolicyInsuranceItems, onLeadUpdated, policyInsuranceTypesById]);

  const handleAttachLabel = useCallback(async () => {
    if (!leadId || selectedLabelIds.length === 0 || !canManage) {
      return;
    }
    try {
      setAttachLoading(true);
      const res = await api(`/api/leads/${leadId}/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labelIds: selectedLabelIds })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke tilknytte label');
      }
      const response = await res.json() as Label | { labels: Label[] };
      const attached = 'labels' in response ? response.labels : [response];
      setLeadLabels((prev) => {
        const nextById = new Map(prev.map((item) => [item.id, item]));
        attached.forEach((label) => nextById.set(label.id, label));
        const next = [...nextById.values()];
        return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
      });
      setAllLabels((prev) => {
        const nextById = new Map(prev.map((item) => [item.id, item]));
        attached.forEach((label) => nextById.set(label.id, label));
        const next = [...nextById.values()];
        return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
      });
      setSelectedLabelIds([]);
      message.success(attached.length === 1 ? 'Label tilknyttet' : `${attached.length} labels tilknyttet`);
      if (onLeadUpdated) {
        await onLeadUpdated(leadId);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke tilknytte label');
    } finally {
      setAttachLoading(false);
    }
  }, [canManage, leadId, onLeadUpdated, selectedLabelIds]);

  const handleDetachLabel = useCallback(
    async (label: Label) => {
      if (!leadId || !canManage) {
        return;
      }
      setRemovingLabelId(label.id);
      try {
        const res = await api(`/api/leads/${leadId}/labels/${label.id}`, {
          method: 'DELETE'
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke fjerne label');
        }
        const data = (await res.json().catch(() => null)) as { labelId: string; leadCount: number } | null;
        setLeadLabels((prev) => prev.filter((item) => item.id !== label.id));
        if (data && typeof data.leadCount === 'number') {
          setAllLabels((prev) => {
            const exists = prev.some((item) => item.id === label.id);
            if (!exists) {
              return prev;
            }
            const next = prev.map((item) =>
              item.id === label.id ? { ...item, leadCount: data.leadCount } : item
            );
            return next.sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
          });
        }
        message.success('Label fjernet');
        if (onLeadUpdated) {
          await onLeadUpdated(leadId);
        }
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke fjerne label');
      } finally {
        setRemovingLabelId(null);
      }
    },
    [canManage, leadId, onLeadUpdated]
  );

  const handleCreateTodoDrawer = useCallback(() => {
    todoForm.setFieldsValue({
      title: '',
      description: '',
      status: 'NEW',
      priority: 'MEDIUM',
      due_at: null,
      assigned_to: null,
      sync_to_calendar: false
    });
    setEditingTodo(null);
    setTodoDrawerOpen(true);
  }, [todoForm]);

  const handleEditTodoDrawer = useCallback(
    (todo: LeadTodo) => {
      todoForm.setFieldsValue({
        title: todo.title,
        description: todo.description || '',
        status: todo.status,
        priority: todo.priority,
        due_at: todo.due_at ? dayjs(todo.due_at) : null,
        assigned_to: todo.assigned_to || null,
        sync_to_calendar: todo.sync_to_calendar
      });
      setEditingTodo(todo);
      setTodoDrawerOpen(true);
    },
    [todoForm]
  );

  const handleTodoDrawerClose = useCallback(() => {
    setTodoDrawerOpen(false);
    setEditingTodo(null);
    todoForm.resetFields();
  }, [todoForm]);

  const handleTodoFormSubmit = useCallback(
    async (values: TodoFormValues) => {
      if (!leadId) {
        return;
      }

      const title = typeof values.title === 'string' ? values.title.trim() : '';
      if (!title) {
        message.error('Titel skal udfyldes');
        return;
      }

      const descriptionValue =
        typeof values.description === 'string' ? values.description.trim() : '';

      const dueValue = values.due_at ? values.due_at.toDate().toISOString() : null;

      const payload: LeadTodoPayload = {
        title,
        description: descriptionValue ? descriptionValue : null,
        status: values.status,
        priority: values.priority,
        due_at: dueValue,
        assigned_to: values.assigned_to ? values.assigned_to : null,
        sync_to_calendar: Boolean(values.sync_to_calendar)
      };

      if (payload.sync_to_calendar && !payload.due_at) {
        message.error('Tilføj en forfaldsdato for at synkronisere til kalenderen');
        return;
      }

      try {
        setSavingTodo(true);
        if (editingTodo) {
          await updateLeadTodo(leadId, editingTodo.id, payload);
          message.success('Opgave opdateret');
        } else {
          await createLeadTodo(leadId, payload);
          message.success('Opgave oprettet');
        }
        handleTodoDrawerClose();
        await loadTodos();
        await onLeadUpdated?.(leadId);
      } catch (error: any) {
        if (error?.errorFields) {
          return;
        }
        message.error(error?.message || 'Kunne ikke gemme opgave');
      } finally {
        setSavingTodo(false);
      }
    },
    [editingTodo, handleTodoDrawerClose, leadId, loadTodos, onLeadUpdated]
  );

  const handleOpenTodoHistory = useCallback(
    async (todo: LeadTodo) => {
      if (!leadId) {
        return;
      }
      setHistoryTodo(todo);
      setTodoHistory([]);
      setTodoHistoryModalOpen(true);
      try {
        setTodoHistoryLoading(true);
        const data = await fetchLeadTodoEvents(leadId, todo.id);
        setTodoHistory(data);
      } catch (error: any) {
        console.error('Failed to load todo history', error);
        message.error(error?.message || 'Kunne ikke hente historik');
      } finally {
        setTodoHistoryLoading(false);
      }
    },
    [leadId]
  );

  const handleTodoHistoryClose = useCallback(() => {
    setTodoHistoryModalOpen(false);
    setHistoryTodo(null);
    setTodoHistory([]);
  }, []);

  const formatAssigneeLabel = useCallback((todo: LeadTodo) => {
    if (todo.assigned_to_name && todo.assigned_to_name.trim()) {
      return todo.assigned_to_name.trim();
    }
    if (todo.assigned_to_email) {
      return todo.assigned_to_email;
    }
    return 'Ingen ansvarlig';
  }, []);

  const handleRegisterCallAttempt = useCallback(async () => {
    if (!leadId || !canManage) {
      return;
    }
    try {
      setCallAttemptLoading(true);
      const res = await api(`/api/leads/${leadId}/call-attempt`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke registrere opkald');
      }
      const data = (await res.json().catch(() => ({}))) as {
        callAttemptCount?: number;
        lastCallAt?: string;
      } | null;
      const attempts = Math.max(1, Number(data?.callAttemptCount ?? callAttemptCount + 1));
      const lastCallDisplay = data?.lastCallAt ? formatDateTimeShort(data.lastCallAt) : null;
      const successText = lastCallDisplay
        ? `Opkald registreret (forsøg #${attempts}) · senest ${lastCallDisplay}`
        : `Opkald registreret (forsøg #${attempts})`;
      message.success(successText);
      await loadDetail();
      await onLeadUpdated?.(leadId);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke registrere opkald');
    } finally {
      setCallAttemptLoading(false);
    }
  }, [canManage, callAttemptCount, leadId, loadDetail, onLeadUpdated]);

  const handleVisibilityChange = useCallback(
    async (hidden: boolean) => {
      if (!leadId || !canManage) {
        return;
      }
      try {
        setVisibilityLoading(true);
        const res = await api(`/api/leads/${leadId}/visibility`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hidden })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke opdatere synlighed');
        }
        await res.json().catch(() => null);
        message.success(hidden ? 'Lead skjult' : 'Lead gjort synlig');
        await loadDetail();
        await onLeadUpdated?.(leadId);
      } catch (error: any) {
        message.error(error?.message || 'Kunne ikke opdatere synlighed');
      } finally {
        setVisibilityLoading(false);
      }
    },
    [canManage, leadId, loadDetail, onLeadUpdated]
  );

  const handleCreateMeeting = useCallback(() => {
    meetingForm.resetFields();
    setEditingMeeting(null);
    setMeetingDrawerOpen(true);
  }, [meetingForm]);

  const handleEditMeeting = useCallback(
    (meeting: LeadMeeting) => {
      meetingForm.setFieldsValue({
        scheduled_for: toLocalInputValue(meeting.scheduled_for),
        location: meeting.location ?? undefined,
        summary: meeting.summary ?? undefined
      });
      setEditingMeeting(meeting);
      setMeetingDrawerOpen(true);
    },
    [meetingForm]
  );

  const handleMeetingDrawerClose = useCallback(() => {
    setMeetingDrawerOpen(false);
    setEditingMeeting(null);
    meetingForm.resetFields();
  }, [meetingForm]);

  const handleMeetingSubmit = useCallback(
    async (values: { scheduled_for?: string; location?: string; summary?: string }) => {
      if (!leadId) {
        return;
      }
      try {
        setSavingMeeting(true);
        const payload = {
          scheduled_for: values.scheduled_for ? new Date(values.scheduled_for).toISOString() : null,
          location: values.location ? values.location.trim() || null : null,
          summary: values.summary ? values.summary.trim() || null : null
        };
        const endpoint = editingMeeting
          ? `/api/leads/${leadId}/meetings/${editingMeeting.id}`
          : `/api/leads/${leadId}/meetings`;
        const method = editingMeeting ? 'PATCH' : 'POST';

        const res = await api(endpoint, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Kunne ikke gemme møde');
        }

        message.success(editingMeeting ? 'Møde opdateret' : 'Møde oprettet');
        await loadDetail();
        meetingForm.resetFields();
        setEditingMeeting(null);
        setMeetingDrawerOpen(false);
      } catch (error: any) {
        if (error?.errorFields) {
          return;
        }
        message.error(error?.message || 'Kunne ikke gemme møde');
      } finally {
        setSavingMeeting(false);
      }
    },
    [editingMeeting, leadId, loadDetail, meetingForm]
  );

  const handleDeleteMeeting = useCallback(
    (meeting: LeadMeeting) => {
      if (!leadId) {
        return;
      }
      Modal.confirm({
        title: 'Slet møde',
        content: 'Er du sikker på, at du vil slette dette møde?',
        okText: 'Slet',
        okType: 'danger',
        cancelText: 'Annuller',
        async onOk() {
          setDeletingMeetingId(meeting.id);
          try {
            const res = await api(`/api/leads/${leadId}/meetings/${meeting.id}`, { method: 'DELETE' });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.error || 'Kunne ikke slette møde');
            }
            message.success('Møde slettet');
            await loadDetail();
          } catch (error: any) {
            message.error(error?.message || 'Kunne ikke slette møde');
          } finally {
            setDeletingMeetingId(null);
          }
        }
      });
    },
    [leadId, loadDetail]
  );

  const meetingDrawerTitle = editingMeeting ? 'Rediger møde' : 'Planlæg møde';
  const meetingDrawerSubmitLabel = editingMeeting ? 'Opdater møde' : 'Gem møde';
  const isLinkingExistingGroup = linkModalMode === 'add';
  const leadSearchEmptyText = leadSearchLoading
    ? 'Søger…'
    : leadSearchQuery.length >= 2 && leadSearchResults.length === 0
      ? 'Ingen leads fundet'
      : 'Skriv mindst 2 tegn for at søge';

  const openLinkModal = useCallback(
    (mode: 'create' | 'add') => {
      if (mode === 'add' && !leadGroup?.id) {
        return;
      }
      setLinkModalMode(mode);
      setSelectedLinkLeadId(null);
      setLeadSearchResults([]);
      setLeadSearchQuery('');
      setLeadSearchLoading(false);
      setLinkModalOpen(true);
    },
    [leadGroup?.id]
  );

  const closeLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setSelectedLinkLeadId(null);
    setLeadSearchResults([]);
    setLeadSearchQuery('');
    setLeadSearchLoading(false);
    if (leadSearchTimeoutRef.current) {
      window.clearTimeout(leadSearchTimeoutRef.current);
      leadSearchTimeoutRef.current = null;
    }
  }, []);

  const handleLeadSearch = useCallback(
    (value: string) => {
      if (leadSearchTimeoutRef.current) {
        window.clearTimeout(leadSearchTimeoutRef.current);
        leadSearchTimeoutRef.current = null;
      }
      const trimmed = value.trim();
      setLeadSearchQuery(trimmed);
      if (trimmed.length < 2) {
        setLeadSearchResults([]);
        setLeadSearchLoading(false);
        return;
      }
      leadSearchTimeoutRef.current = window.setTimeout(async () => {
        leadSearchTimeoutRef.current = null;
        setLeadSearchLoading(true);
        try {
          const params = new URLSearchParams();
          params.set('q', trimmed);
          params.set('includeHidden', 'true');
          const res = await api(`/api/leads?${params.toString()}`);
          const payload = await res.json().catch(() => []);
          if (!res.ok) {
            throw new Error((payload as any)?.error || 'Kunne ikke søge efter leads');
          }
          const rows = Array.isArray(payload) ? payload : [];
          const excluded = new Set<string>();
          if (lead?.id) {
            excluded.add(lead.id);
          }
          if (leadGroup?.members) {
            for (const member of leadGroup.members) {
              excluded.add(member.id);
            }
          }
          setLeadSearchResults(
            rows
              .filter((row: any) => !excluded.has(row.id))
              .map((row: any) => ({
                id: row.id,
                company_name: row.company_name,
                cvrnummer: row.cvrnummer ?? null,
                status: row.status
              }))
          );
        } catch (error: any) {
          console.error('Failed to search leads', error);
          message.error(error?.message || 'Kunne ikke søge efter leads');
        } finally {
          setLeadSearchLoading(false);
        }
      }, 300);
    },
    [lead?.id, leadGroup]
  );

  const handleLinkSubmit = useCallback(async () => {
    if (!leadId || !selectedLinkLeadId) {
      return;
    }
    setLinkModalSubmitting(true);
    try {
      const headers = new Headers({ 'Content-Type': 'application/json' });
      let endpoint = '/api/lead-groups';
      let body: Record<string, unknown> = { lead_ids: [leadId, selectedLinkLeadId] };
      if (linkModalMode === 'add' && leadGroup?.id) {
        endpoint = `/api/lead-groups/${leadGroup.id}/leads`;
        body = { lead_ids: [selectedLinkLeadId] };
      }
      const res = await api(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((payload as any)?.error || 'Kunne ikke linke lead');
      }
      const targetGroupId = (payload as any)?.group?.id ?? leadGroup?.id ?? null;
      message.success(
        linkModalMode === 'add' && leadGroup?.id
          ? 'Lead tilføjet til samlingen'
          : 'Samlekunde oprettet'
      );
      closeLinkModal();
      await loadDetail();
      if (targetGroupId) {
        await loadLeadGroup(targetGroupId);
      } else {
        setLeadGroup(null);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke linke lead');
    } finally {
      setLinkModalSubmitting(false);
    }
  }, [leadId, selectedLinkLeadId, linkModalMode, leadGroup, closeLinkModal, loadDetail, loadLeadGroup]);

  const handleRemoveMember = useCallback(
    (memberId: string) => {
      if (!leadGroup?.id) {
        return;
      }
      Modal.confirm({
        title: 'Fjern lead fra samling',
        content: 'Er du sikker på, at du vil fjerne dette lead fra samlingen?',
        okText: 'Fjern',
        okType: 'danger',
        cancelText: 'Annuller',
        async onOk() {
          setRemovingGroupMemberId(memberId);
          try {
            const res = await api(`/api/lead-groups/${leadGroup.id}/leads/${memberId}`, {
              method: 'DELETE'
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
              throw new Error((payload as any)?.error || 'Kunne ikke fjerne lead');
            }
            message.success('Lead fjernet fra samlingen');
            await loadDetail();
            if ((payload as any)?.group?.id) {
              await loadLeadGroup((payload as any).group.id);
            } else {
              setLeadGroup(null);
            }
          } catch (error: any) {
            message.error(error?.message || 'Kunne ikke fjerne lead');
          } finally {
            setRemovingGroupMemberId(null);
          }
        }
      });
    },
    [leadGroup?.id, loadDetail, loadLeadGroup]
  );

  const urgentPropertyAlerts = insuranceEvents?.events.filter(
    (event) => event.status === 'OPEN' && (event.severity === 'CRITICAL' || event.severity === 'HIGH')
  ) || [];

  return (
    <Space className="lead-detail-page" direction="vertical" size={18} style={{ width: '100%' }}>
      {analysisMessageContextHolder}
      <Button
        onClick={() =>
          onBack(backLocationState)
        }
      >
        Tilbage til oversigt
      </Button>

      {urgentPropertyAlerts.length ? (
        <Alert
          type="error"
          showIcon
          message={`${urgentPropertyAlerts.length} væsentlige ændringer i ejendomsporteføljen kræver vurdering`}
          description={
            <Space direction="vertical" size={4}>
              {urgentPropertyAlerts.slice(0, 3).map((event) => (
                <Text key={event.id}>
                  <Tag color={event.severity === 'CRITICAL' ? 'red' : 'orange'}>{event.severity}</Tag> {event.title}
                </Text>
              ))}
            </Space>
          }
        />
      ) : null}

      <Tabs
        className="lead-detail-tabs"
        destroyInactiveTabPane={false}
        items={[
          {
            key: 'overview',
            label: 'Overblik',
            children: (
              <Card className="lead-detail-hero" loading={loading} title={lead?.company_name ?? 'Lead'}>
        {lead && (
          <>
            <Space className="lead-detail-actionbar" size={8} wrap>
              <Tag color={isHidden ? 'default' : 'blue'}>
                {isHidden ? 'Skjult lead' : 'Synligt lead'}
              </Tag>
              <Tooltip title={callAttemptCount > 0 ? lastCallFull || undefined : undefined}>
                <Tag color={callAttemptCount > 0 ? 'processing' : 'default'}>
                  Opkald: {callAttemptCount}
                  {callAttemptCount > 0 && lastCallShort ? ` · senest ${lastCallShort}` : ''}
                </Tag>
              </Tooltip>
              <Tooltip title={tierDescription || undefined}>
                <Tag color={tierTagColor}>{tierLabel}</Tag>
              </Tooltip>
              {canManage && (
                <>
                  <Button onClick={handleRegisterCallAttempt} loading={callAttemptLoading}>
                    Registrer opkald uden svar
                  </Button>
                  <Button
                    onClick={() => handleVisibilityChange(!isHidden)}
                    loading={visibilityLoading}
                  >
                    {isHidden ? 'Markér som synlig' : 'Skjul lead'}
                  </Button>
                </>
              )}
            </Space>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} xl={8}>
                <div className="lead-detail-metric">
                  <Text type="secondary">Status</Text>
                  <div>
                    <Tag color={statusTagColors[lead.status] || 'default'}>
                      {statusLabelMap.get(lead.status) || lead.status}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <div className="lead-detail-metric">
                  <Text type="secondary">Kontakt</Text>
                  <Text strong>{lead.contact_name || '—'}</Text>
                  <Text>
                    {lead.contact_email ? (
                      <a href={`mailto:${lead.contact_email}`}>{lead.contact_email}</a>
                    ) : (
                      '—'
                    )}
                  </Text>
                  <Text>{lead.contact_phone || '—'}</Text>
                </div>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <div className="lead-detail-metric">
                  <Text type="secondary">CVR og ansatte</Text>
                  <Text strong>{formattedCvr || '—'}</Text>
                  <Text>{employeesDisplay ?? '—'}{employeesYear ? ` (${employeesYear})` : ''}</Text>
                </div>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <div className="lead-detail-metric">
                  <Text type="secondary">Kontraktværdi</Text>
                  <Text strong>{formatAmount(lead.contract_value_annual, lead.currency)}</Text>
                </div>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <div className="lead-detail-metric">
                  <Text type="secondary">Provision</Text>
                  <Text strong>{commissionDisplay}</Text>
                </div>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <div className="lead-detail-metric">
                  <Text type="secondary">Referrer</Text>
                  <Text strong>{lead.ref_full_name || lead.ref_email || 'Ukendt'}</Text>
                  <Text type="secondary">Opdateret {formatDateTime(lead.last_updated)}</Text>
                </div>
              </Col>
            </Row>
          </>
        )}
              </Card>
            )
          },
          {
            key: 'work',
            label: 'Arbejde',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card title="Detaljer">
                  {canManage ? (
                    <Form form={notesForm} layout="vertical" onFinish={handleSaveNotes}>
                      <Form.Item name="cvrnummer" label="CVR-nummer" rules={[{ validator: validateOptionalCvr }]}>
                        <Input placeholder="F.eks. 12345678" maxLength={20} />
                      </Form.Item>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item name="contact_name" label="Kontaktperson">
                            <Input placeholder="Navn" allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item name="contact_email" label="E-mail" rules={[{ type: 'email', message: 'Ugyldig e-mailadresse' }]}>
                            <Input placeholder="fx navn@example.dk" allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item name="contact_phone" label="Telefon">
                        <Input placeholder="Telefonnummer" allowClear maxLength={40} />
                      </Form.Item>
                      <Form.Item name="notes" label="Interne noter">
                        <Input.TextArea rows={4} placeholder="Tilføj noter om leadet" allowClear />
                      </Form.Item>
                      <Button type="primary" htmlType="submit" loading={savingNotes}>
                        Gem noter
                      </Button>
                    </Form>
                  ) : (
                    <Space direction="vertical" size={8}>
                      <Paragraph style={{ whiteSpace: 'pre-line' }}>{lead?.notes || 'Ingen noter'}</Paragraph>
                      <div>
                        <Text strong>CVR-nummer:</Text>{' '}
                        {formattedCvr || 'Ikke angivet'}
                      </div>
                    </Space>
                  )}
                </Card>

                {canManage && (
                  <Card
                    title="Opgaver"
                    extra={
                      <Tooltip title="Ny opgave">
                        <Button
                          icon={<PlusOutlined />}
                          type="primary"
                          size="small"
                          onClick={handleCreateTodoDrawer}
                          disabled={!leadId}
                        />
                      </Tooltip>
                    }
                    bodyStyle={{ padding: 0 }}
                  >
                    <List
                      loading={todosLoading}
                      dataSource={todos}
                      locale={{ emptyText: 'Ingen opgaver endnu' }}
                      renderItem={(todo) => {
                        const isClosed = todo.status === 'DONE' || todo.status === 'CANCELLED';
                        const isOverdue =
                          !!todo.due_at && !isClosed && new Date(todo.due_at).getTime() < Date.now();

                        return (
                          <List.Item
                            key={todo.id}
                            actions={[
                              <Tooltip key="history" title="Se historik">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<HistoryOutlined />}
                                  onClick={() => handleOpenTodoHistory(todo)}
                                />
                              </Tooltip>,
                              <Tooltip key="edit" title="Rediger">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditTodoDrawer(todo)}
                                />
                              </Tooltip>
                            ]}
                          >
                            <List.Item.Meta
                              title={
                                <Space size={8} wrap>
                                  <Text strong>{todo.title}</Text>
                                  <Tag color={todoStatusColors[todo.status] || 'default'}>
                                    {todoStatusLabels[todo.status] || todo.status}
                                  </Tag>
                                  <Tag color={todoPriorityColors[todo.priority] || 'default'}>
                                    {todoPriorityLabels[todo.priority] || todo.priority}
                                  </Tag>
                                  {todo.sync_to_calendar ? (
                                    <Tooltip title="Synkroniseret til kalender">
                                      <CalendarOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                  ) : null}
                                </Space>
                              }
                              description={
                                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                                  {todo.description ? (
                                    <Space direction="vertical" size={2}>
                                      <Text style={{ whiteSpace: 'pre-wrap' }}>{todo.description}</Text>
                                      {extractWorkListLink(todo.description) ? (
                                        <a href={extractWorkListLink(todo.description) as string}>Åbn arbejdsliste</a>
                                      ) : null}
                                    </Space>
                                  ) : (
                                    <Text type="secondary">Ingen beskrivelse</Text>
                                  )}
                                  <Space size={16} wrap>
                                    <Tooltip title={todo.due_at ? formatDateTimeFull(todo.due_at) : undefined}>
                                      <Text type={isOverdue ? 'danger' : 'secondary'}>
                                        Forfald: {todo.due_at ? formatDateTimeShort(todo.due_at) : 'Ingen'}
                                      </Text>
                                    </Tooltip>
                                    <Text type="secondary">Ansvarlig: {formatAssigneeLabel(todo)}</Text>
                                    <Text type="secondary">
                                      Opdateret: {formatDateTimeShort(todo.updated_at) || 'Ukendt'}
                                    </Text>
                                  </Space>
                                </Space>
                              }
                            />
                          </List.Item>
                        );
                      }}
                    />
                  </Card>
                )}

                <Card
                  title="Møder"
                  extra={
                    canManage ? (
                      <Tooltip title="Planlæg nyt møde">
                        <Button
                          icon={<PlusOutlined />}
                          type="primary"
                          size="small"
                          onClick={handleCreateMeeting}
                        />
                      </Tooltip>
                    ) : undefined
                  }
                  bodyStyle={{ padding: 0 }}
                >
                  <List
                    dataSource={meetings}
                    locale={{ emptyText: canManage ? 'Ingen møder endnu – planlæg det første' : 'Ingen møder endnu' }}
                    renderItem={(item) => (
                      <List.Item
                        key={item.id}
                        actions={
                          canManage
                            ? [
                                <Tooltip key="edit" title="Rediger">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditMeeting(item)}
                                  />
                                </Tooltip>,
                                <Tooltip key="delete" title="Slet">
                                  <Button
                                    danger
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteMeeting(item)}
                                    loading={deletingMeetingId === item.id}
                                  />
                                </Tooltip>
                              ]
                            : undefined
                        }
                      >
                        <List.Item.Meta
                          title={
                            <Space direction="vertical" size={0}>
                              <Text strong>{item.summary || 'Møde'}</Text>
                              <Text type="secondary">{formatDateTime(item.scheduled_for)}</Text>
                            </Space>
                          }
                          description={
                            <Space direction="vertical" size={0}>
                              <Text>{item.location || 'Ingen lokation angivet'}</Text>
                              <Text type="secondary">Oprettet {formatDateTime(item.created_at)}</Text>
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </Card>
              </Space>
            )
          },
          {
            key: 'financial-statement',
            label: 'Regnskab',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card
                  title="Seneste regnskabstal"
                  extra={
                    <Button
                      icon={<ReloadOutlined />}
                      type={financialStatement ? 'default' : 'primary'}
                      loading={financialStatementLoading}
                      disabled={!leadId || !savedLeadCvr}
                      onClick={() => void loadFinancialStatement()}
                    >
                      {financialStatement ? 'Opdater fra Virk' : 'Hent seneste regnskabstal'}
                    </Button>
                  }
                >
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    {!savedLeadCvr ? <Alert type="warning" showIcon message="Leaden mangler et CVR-nummer, så regnskabsdata kan ikke hentes." /> : null}
                    {financialStatementError ? <Alert type="error" showIcon message={financialStatementError} /> : null}
                    {!financialStatement && !financialStatementLoading && savedLeadCvr && !financialStatementError ? (
                      <Text type="secondary">Der er endnu ikke hentet og gemt regnskabstal for leadet.</Text>
                    ) : null}
                    {financialStatement?.status === 'NO_XBRL' ? (
                      <Alert type="info" showIcon message="Virk har ingen brugbar XBRL-regnskabsfil for denne virksomhed." />
                    ) : null}
                    {financialStatement?.status === 'AVAILABLE' ? (
                      <>
                        <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }}>
                          <Descriptions.Item label="Regnskabsperiode">
                            {[financialStatement.reportingPeriod?.startDate, financialStatement.reportingPeriod?.endDate].filter(Boolean).join(' til ') || 'Ikke oplyst'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Offentliggjort">{formatDateTime(financialStatement.publishedAt)}</Descriptions.Item>
                          <Descriptions.Item label="Revision">{financialStatement.auditType || 'Ikke oplyst'}</Descriptions.Item>
                          <Descriptions.Item label="Sagsnummer">{financialStatement.caseNumber || 'Ikke oplyst'}</Descriptions.Item>
                          <Descriptions.Item label="Hentet">{formatDateTime(financialStatement.fetchedAt)}</Descriptions.Item>
                          <Descriptions.Item label="Kilde">
                            {financialStatement.sourceUrl ? <a href={financialStatement.sourceUrl} target="_blank" rel="noreferrer">Åbn XBRL hos Virk</a> : 'Ikke oplyst'}
                          </Descriptions.Item>
                        </Descriptions>
                        {[
                          ['Resultat', financialStatement.incomeStatement],
                          ['Balance og likviditet', financialStatement.balanceSheet],
                          ['Beregnede nøgletal', financialStatement.ratios]
                        ].map(([title, metrics]) => {
                          const rows = metrics as FinancialMetric[];
                          return rows.length ? (
                            <div key={title as string}>
                              <Text strong>{title as string}</Text>
                              <Table<FinancialMetric>
                                size="small"
                                rowKey="label"
                                pagination={false}
                                dataSource={rows}
                                columns={[
                                  {
                                    title: 'Nøgletal',
                                    dataIndex: 'label',
                                    key: 'label',
                                    render: (value, row) => (
                                      <Space size={4}>
                                        <span>{value}</span>
                                        {FINANCIAL_METRIC_EXPLANATIONS[value] ? (
                                          <Tooltip title={FINANCIAL_METRIC_EXPLANATIONS[value]}>
                                            <InfoCircleOutlined style={{ color: '#1677ff' }} />
                                          </Tooltip>
                                        ) : null}
                                        {row.calculated ? <Tag>Beregnede</Tag> : null}
                                      </Space>
                                    )
                                  },
                                  { title: 'Seneste år', key: 'current', align: 'right', render: (_, row) => formatFinancialValue(row.current, row.calculated) },
                                  { title: 'Forrige år', key: 'previous', align: 'right', render: (_, row) => formatFinancialValue(row.previous, row.calculated) },
                                  { title: 'Udvikling', key: 'change', align: 'right', render: (_, row) => formatFinancialChange(row) }
                                ]}
                              />
                            </div>
                          ) : null;
                        })}
                      </>
                    ) : null}
                  </Space>
                </Card>
              </Space>
            )
          },
          {
            key: 'policies',
            label: 'Policer',
            children: (
              <>

      {leadId && (
        <Card title="Policedokumenter" loading={policyLoading}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size={12} wrap style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space size={8} wrap>
                <Text strong>Samlet eksport</Text>
                <Text type="secondary">{completedPolicyCount} analyserede dokumenter klar</Text>
              </Space>
              <Space size={8} wrap>
                <Select
                  value={combinedPolicyExportType}
                  style={{ minWidth: 220 }}
                  onChange={(value) => setCombinedPolicyExportType(value as PolicyExportType)}
                  options={[
                    { value: 'tender', label: 'Udbudsmateriale (PDF)' },
                    { value: 'customer-agreement', label: 'Aftaledokument (PDF)' }
                  ]}
                />
                <Button
                  onClick={handleCombinedPolicyPdfExport}
                  loading={combinedPolicyExportLoading}
                  disabled={!leadId || completedPolicyCount === 0 || combinedPolicyExportLoading}
                >
                  Eksportér samlet PDF
                </Button>
              </Space>
            </Space>

            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Upload
                multiple
                fileList={policyFileList}
                beforeUpload={(file) => {
                  const maxSize = 25 * 1024 * 1024;
                  if (file.size > maxSize) {
                    message.error(`${file.name} er større end 25 MB`);
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                onChange={({ fileList }) => {
                  const limited = fileList.slice(-6);
                  if (fileList.length > limited.length) {
                    message.warning('Der kan max uploades 6 filer ad gangen');
                  }
                  setPolicyFileList(limited);
                }}
              >
                <Button icon={<UploadOutlined />}>Vælg dokumenter</Button>
              </Upload>
              <Space size={12} wrap>
                <Button
                  type="primary"
                  onClick={handlePolicyUpload}
                  loading={policyUploading}
                  disabled={!leadId || policyFileList.length === 0}
                >
                  Upload
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleAnalyzeAllPolicies}
                  loading={policyAnalyzeAllLoading}
                  disabled={!leadId || policyDocuments.length === 0 || policyAnalyzeAllBackground}
                >
                  AI-analyser alle uploadede
                </Button>
                {policyAnalyzeAllLoading ? <Tag color="processing">AI-analyse igang...</Tag> : null}
                {policyAnalyzeAllBackground ? (
                  <Tag color="processing">
                    AI-analyse i baggrunden{pendingPolicyCount > 0 ? ` (${pendingPolicyCount} under analyse)` : ''}
                  </Tag>
                ) : null}
                {!policyAnalyzeAllLoading ? (
                  <Text type="secondary">{notCompletedPolicyCount} dokumenter mangler analyse</Text>
                ) : null}
                <Text type="secondary">PDF/DOCX/TXT · max 25 MB pr. fil</Text>
              </Space>
              {canManage && !hideInsurance && availableInsuranceTypes.length === 0 && !insuranceTypesLoading ? (
                <Text type="secondary">
                  Opret forsikringstyper under Tilpasning for at tilføje dem til policer.
                </Text>
              ) : null}
            </Space>

            {policyDocuments.length === 0 ? (
              <Text type="secondary">Ingen dokumenter i udbudsværkstedet endnu.</Text>
            ) : (
              <Row gutter={16}>
                <Col xs={24} xl={9}>
                  <Card
                    size="small"
                    title="Køretøjer"
                    extra={<Text type="secondary">{filteredPolicyDocuments.length}/{policyDocuments.length}</Text>}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Input
                        allowClear
                        value={policySearchTerm}
                        onChange={(event) => setPolicySearchTerm(event.target.value)}
                        placeholder="Søg reg.nr., køretøj eller dokument"
                      />
                      <Space size={8} wrap>
                        <Select
                          value={policyScopeFilter}
                          style={{ minWidth: 120 }}
                          onChange={(value) => setPolicyScopeFilter(value as PolicyScopeFilter)}
                          options={[
                            { value: 'MOTOR', label: 'Kun motor' },
                            { value: 'ALL', label: 'Alle typer' }
                          ]}
                        />
                        <Select
                          value={policyStatusFilter}
                          style={{ minWidth: 140 }}
                          onChange={(value) => setPolicyStatusFilter(value as PolicyStatusFilter)}
                          options={[
                            { value: 'ALL', label: 'Alle statuser' },
                            { value: 'PENDING', label: 'Under analyse' },
                            { value: 'COMPLETED', label: 'Færdig' },
                            { value: 'FAILED', label: 'Fejlet' }
                          ]}
                        />
                        <Select
                          value={policyReadinessFilter}
                          style={{ minWidth: 150 }}
                          onChange={(value) => setPolicyReadinessFilter(value as PolicyReadinessFilter)}
                          options={[
                            { value: 'ALL', label: 'Alle readiness' },
                            { value: 'READY', label: 'Klar til eksport' },
                            { value: 'MISSING', label: 'Mangler felter' }
                          ]}
                        />
                      </Space>

                      <List
                        size="small"
                        dataSource={filteredPolicyDocuments}
                        locale={{ emptyText: 'Ingen dokumenter matcher filteret.' }}
                        renderItem={(doc) => {
                          const selected = selectedPolicyDocumentId === doc.id;
                          const readiness = policyMotorReadiness(doc);
                          const registrationNumber = policyFieldValue(doc, 'motor.registration_number');
                          const vehicleDescription = policyFieldValue(doc, 'motor.vehicle_description');
                          const coverages = policyCoverageTags(doc);
                          const missingFieldLabels = readiness?.missingKeys.map((key) => MOTOR_FIELD_LABELS[key] || key) || [];
                          return (
                            <List.Item
                              key={doc.id}
                              style={{
                                cursor: 'pointer',
                                background: selected ? '#e6f4ff' : 'transparent',
                                borderRadius: 8,
                                paddingInline: 8
                              }}
                              onClick={() => setSelectedPolicyDocumentId(doc.id)}
                            >
                              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                <Space size={8} wrap>
                                  <Text strong>{registrationNumber || doc.file_name}</Text>
                                  <Tag color={policyStatusColors[doc.analysis_status] || 'default'}>{getPolicyStatusLabel(doc)}</Tag>
                                  {readiness ? (
                                    <Tag color={readiness.isReady ? 'success' : 'warning'}>
                                      Readiness {readiness.completionLabel}
                                    </Tag>
                                  ) : null}
                                </Space>
                                <Text type="secondary">{vehicleDescription || doc.file_name}</Text>
                                {coverages.length ? (
                                  <Space size={4} wrap>
                                    {coverages.slice(0, 4).map((coverage) => (
                                      <Tag key={`${doc.id}-${coverage}`}>{coverage}</Tag>
                                    ))}
                                  </Space>
                                ) : null}
                                {missingFieldLabels.length > 0 ? (
                                  <Text type="warning">Mangler: {missingFieldLabels.slice(0, 3).join(', ')}{missingFieldLabels.length > 3 ? '...' : ''}</Text>
                                ) : null}
                              </Space>
                            </List.Item>
                          );
                        }}
                      />
                    </Space>
                  </Card>
                </Col>

                <Col xs={24} xl={15}>
                  {selectedPolicyDocument ? (
                    <Card
                      size="small"
                      title={
                        <Space size={8} wrap>
                          <Text strong>{selectedPolicyDocument.file_name}</Text>
                          <Tag color={policyStatusColors[selectedPolicyDocument.analysis_status] || 'default'}>
                            {getPolicyStatusLabel(selectedPolicyDocument)}
                          </Tag>
                          {selectedPolicyDocument.analysis_model ? <Tag>{selectedPolicyDocument.analysis_model}</Tag> : null}
                          {policyMotorReadiness(selectedPolicyDocument) ? (
                            <Tag color={policyMotorReadiness(selectedPolicyDocument)?.isReady ? 'success' : 'warning'}>
                              Readiness {policyMotorReadiness(selectedPolicyDocument)?.completionLabel}
                            </Tag>
                          ) : null}
                        </Space>
                      }
                      extra={
                        <Space size={8} wrap>
                          <Button size="small" onClick={() => handlePolicyViewOriginal(selectedPolicyDocument)}>Vis original</Button>
                          <Button size="small" onClick={() => handlePolicyDownload(selectedPolicyDocument)}>Download</Button>
                          {canManage ? (
                            <Button
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeletePolicy(selectedPolicyDocument)}
                            >
                              Slet
                            </Button>
                          ) : null}
                        </Space>
                      }
                    >
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Text type="secondary">
                          Uploadet {formatDateTimeShort(selectedPolicyDocument.uploaded_at)} · {formatPolicyFileSize(selectedPolicyDocument.file_size)}
                        </Text>
                        {!hideInsurance ? (
                          <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <Space size={8} wrap>
                              <Text strong>Forsikringstyper</Text>
                              {canManage ? (
                                <Button
                                  icon={<PlusOutlined />}
                                  size="small"
                                  onClick={() => handleOpenPolicyInsuranceModal(selectedPolicyDocument.id)}
                                  disabled={!leadId || availableInsuranceTypes.length === 0 || insuranceTypesLoading}
                                >
                                  Tilføj / rediger
                                </Button>
                              ) : null}
                            </Space>

                            {policyInsuranceLoadingById[selectedPolicyDocument.id] ? (
                              <Text type="secondary">Henter forsikringstyper...</Text>
                            ) : (policyInsuranceTypesById[selectedPolicyDocument.id] || []).length === 0 ? (
                              <Text type="secondary">Ingen forsikringstyper tilknyttet denne police.</Text>
                            ) : (
                              <List
                                size="small"
                                dataSource={policyInsuranceTypesById[selectedPolicyDocument.id] || []}
                                renderItem={(item) => (
                                  <List.Item
                                    key={`${selectedPolicyDocument.id}-${item.id}`}
                                    actions={
                                      canManage
                                        ? [
                                            <Button
                                              key="remove"
                                              type="text"
                                              danger
                                              size="small"
                                              onClick={() => handleRemovePolicyInsuranceType(selectedPolicyDocument.id, String(item.insuranceTypeId))}
                                              loading={removingPolicyInsuranceKey === `${selectedPolicyDocument.id}:${String(item.insuranceTypeId)}`}
                                            >
                                              Fjern
                                            </Button>
                                          ]
                                        : undefined
                                    }
                                  >
                                    <List.Item.Meta
                                      title={
                                        <Space size={8} wrap>
                                          <Text strong>{item.title}</Text>
                                          {item.category ? <Tag>{item.category}</Tag> : null}
                                          {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                                        </Space>
                                      }
                                      description={
                                        item.description ? (
                                          <Text>{item.description}</Text>
                                        ) : (
                                          <Text type="secondary">Ingen beskrivelse</Text>
                                        )
                                      }
                                    />
                                  </List.Item>
                                )}
                              />
                            )}
                          </Space>
                        ) : null}
                        {policyTemplateLoadingById[selectedPolicyDocument.id] ? (
                          <Text type="secondary">Henter relevante udbudsskabeloner...</Text>
                        ) : policyTemplatesById[selectedPolicyDocument.id] ? (
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong>Udbudsskabelon</Text>
                            {policyTemplatesById[selectedPolicyDocument.id].policyInsuranceTypes.length === 0 ? (
                              <Text type="secondary">Tilknyt forsikringstyper til policen, før en udbudsskabelon kan vælges.</Text>
                            ) : policyTemplatesById[selectedPolicyDocument.id].templates.length === 0 ? (
                              <Text type="secondary">Ingen aktiv og analyseret udbudsskabelon matcher policens forsikringstyper.</Text>
                            ) : (
                              <Select
                                value={policyTemplatesById[selectedPolicyDocument.id].selectedTemplateId || undefined}
                                placeholder="Vælg udbudsskabelon"
                                loading={policyTemplateSavingById[selectedPolicyDocument.id]}
                                disabled={policyTemplateSavingById[selectedPolicyDocument.id]}
                                style={{ maxWidth: 420, width: '100%' }}
                                onChange={(value) => void handlePolicyTemplateChange(selectedPolicyDocument.id, value)}
                                options={policyTemplatesById[selectedPolicyDocument.id].templates.map((template) => ({
                                  value: template.id,
                                  label: `${template.name} (${template.fieldCount} felter)`
                                }))}
                              />
                            )}
                          </Space>
                        ) : null}
                        {renderPolicyAnalysisActions(selectedPolicyDocument)}
                        <Collapse
                          ghost
                          items={[
                            {
                              key: 'analysis',
                              label: 'AI analyse',
                              children: renderPolicyAnalysis(selectedPolicyDocument)
                            }
                          ]}
                        />
                      </Space>
                    </Card>
                  ) : (
                    <Card size="small">
                      <Text type="secondary">Vælg et dokument i listen for at se detaljer.</Text>
                    </Card>
                  )}
                </Col>
              </Row>
            )}
          </Space>
        </Card>
      )}
              </>
            )
          },
          {
            key: 'needs-analysis',
            label: 'Behovsanalyse',
            children: leadId ? <NeedsAnalysisPanel leadId={leadId} canManage={canManage} /> : null
          },
          {
            key: 'tender',
            label: 'Udbud',
            children: (
              <>

      {leadId ? (
        <Card
          title="Udbudsværksted"
          extra={
            <Space size={8} wrap>
              <Tag color="blue">Klar: {tenderWorkspacePolicies.length}</Tag>
              <Tag color="default">Mangler skabelon: {Math.max(policyDocuments.length - tenderWorkspacePolicies.length, 0)}</Tag>
              <Button
                size="small"
                onClick={handlePrepareAllTenderWorkspaces}
                loading={prepareTenderWorkspacesLoading}
                disabled={policyDocuments.length === 0 || prepareTenderWorkspacesLoading}
              >
                Klargør alle
              </Button>
            </Space>
          }
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {policyDocuments.length === 0 ? (
              <Text type="secondary">Upload mindst én police for at åbne udbudsværkstedet.</Text>
            ) : tenderWorkspacePolicies.length === 0 ? (
              <Text type="secondary">
                Ingen policer er klar endnu. Vælg en udbudsskabelon på mindst én police i policy-panelet.
              </Text>
            ) : (
              <>
                {tenderAmbiguousPolicies.length > 0 ? (
                  <Card
                    size="small"
                    title="Policer med flere skabelonkandidater"
                    extra={<Tag color="gold">Kræver valg: {tenderAmbiguousPolicies.length}</Tag>}
                  >
                    <List
                      size="small"
                      dataSource={tenderAmbiguousPolicies}
                      renderItem={({ document, context }) => (
                        <List.Item key={`ambiguous-${document.id}`}>
                          <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Space size={8} wrap>
                              <Text strong>{policyFieldValue(document, 'motor.registration_number') || document.file_name}</Text>
                              <Tag>{getPolicyStatusLabel(document)}</Tag>
                              <Text type="secondary">{context?.templates.length || 0} kandidater</Text>
                            </Space>
                            <Select
                              placeholder="Vælg skabelon"
                              value={context?.selectedTemplateId || undefined}
                              loading={policyTemplateSavingById[document.id]}
                              disabled={policyTemplateSavingById[document.id]}
                              style={{ maxWidth: 460, width: '100%' }}
                              onChange={(value) => void handlePolicyTemplateChange(document.id, value)}
                              options={(context?.templates || []).map((template) => ({
                                value: template.id,
                                label: `${template.name} (${template.fieldCount} felter)`
                              }))}
                            />
                          </Space>
                        </List.Item>
                      )}
                    />
                  </Card>
                ) : null}

                <Space size={12} wrap>
                  <Text strong>Aktiv police</Text>
                  <Select
                    value={selectedTenderPolicy?.id || undefined}
                    style={{ minWidth: 380 }}
                    onChange={(value) => setSelectedTenderPolicyId(value)}
                    options={tenderWorkspacePolicies.map((document) => ({
                      value: document.id,
                      label: `${policyFieldValue(document, 'motor.registration_number') || document.file_name} · ${getPolicyStatusLabel(document)}`
                    }))}
                  />
                </Space>

                <Space size={8} wrap>
                  {tenderWorkspacePolicies.map((document) => {
                    const active = selectedTenderPolicy?.id === document.id;
                    const registration = policyFieldValue(document, 'motor.registration_number');
                    return (
                      <Tag
                        key={document.id}
                        color={active ? 'processing' : 'default'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedTenderPolicyId(document.id)}
                      >
                        {registration || document.file_name}
                      </Tag>
                    );
                  })}
                </Space>

                {selectedTenderPolicy ? (
                  <TenderWorkspace
                    key={selectedTenderPolicy.id}
                    leadId={leadId}
                    policyId={selectedTenderPolicy.id}
                    canManage={canManage}
                    analyzedPolicyCount={selectedTenderPolicy.analysis_status === 'COMPLETED' ? 1 : 0}
                  />
                ) : null}
              </>
            )}
          </Space>
        </Card>
      ) : null}
              </>
            )
          },
          {
            key: 'offers',
            label: 'Tilbud',
            children: leadId ? <CustomerOfferPanel leadId={leadId} canManage={canManage} /> : null
          },
          {
            key: 'relations',
            label: 'Relationer',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>

      {lead && (lead.lead_group_id || canManage) ? (
        <Card
          title="Samlekunde"
          loading={Boolean(lead.lead_group_id) && leadGroupLoading}
          extra={
            leadGroup ? (
              <Tag color="blue">{leadGroup.members.length} lead{leadGroup.members.length === 1 ? '' : 's'}</Tag>
            ) : undefined
          }
        >
          {leadGroup ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Space size={8} align="center" wrap>
                <Text strong>{leadGroup.display_name}</Text>
                <Tag color="blue">{leadGroup.members.length} lead{leadGroup.members.length === 1 ? '' : 's'}</Tag>
                <Text type="secondary">
                  Opdateret {formatDateTime(leadGroup.updated_at)}
                </Text>
              </Space>
              {leadGroup.notes ? <Paragraph>{leadGroup.notes}</Paragraph> : null}
              <List
                size="small"
                dataSource={leadGroup.members}
                bordered
                locale={{ emptyText: 'Ingen tilknyttede leads' }}
                renderItem={(member) => {
                  const isCurrent = member.id === lead.id;
                  const statusLabel = member.status
                    ? statusLabelMap.get(member.status) || member.status
                    : 'Ukendt status';
                  const statusColor = member.status
                    ? statusTagColors[member.status] || 'default'
                    : 'default';
                  return (
                    <List.Item
                      key={member.id}
                      actions={
                        canManage
                          ? [
                              <Button
                                key="remove"
                                type="text"
                                danger
                                size="small"
                                loading={removingGroupMemberId === member.id}
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                Fjern
                              </Button>
                            ]
                          : undefined
                      }
                    >
                      <Space direction="vertical" size={0} style={{ width: '100%' }}>
                        <Space size={8} wrap align="center">
                          <Text strong>{member.company_name || 'Lead'}</Text>
                          {member.cvrnummer ? (
                            <Text type="secondary">
                              CVR {formatCvrNumber(member.cvrnummer)}
                            </Text>
                          ) : null}
                          <Tag color={statusColor}>{statusLabel}</Tag>
                          {isCurrent ? <Tag color="processing">Aktuelt lead</Tag> : null}
                        </Space>
                        <Text type="secondary">Oprettet {formatDateTime(member.created_at)}</Text>
                      </Space>
                    </List.Item>
                  );
                }}
              />
              {canManage ? (
                <Space size={12} wrap>
                  <Button type="primary" onClick={() => openLinkModal('add')}>
                    Tilføj lead til samling
                  </Button>
                  <Button
                    danger
                    onClick={() => lead?.id && handleRemoveMember(lead.id)}
                    loading={Boolean(lead?.id) && removingGroupMemberId === lead?.id}
                  >
                    Fjern dette lead fra samlingen
                  </Button>
                </Space>
              ) : null}
            </Space>
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Text>Dette lead er ikke en del af en samlekunde endnu.</Text>
              {canManage ? (
                <Button type="primary" onClick={() => openLinkModal('create')} disabled={!leadId}>
                  Link til andet lead
                </Button>
              ) : null}
            </Space>
          )}
        </Card>
      ) : null}

      <Card loading={loading} title="Labels">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {sortedLeadLabels.length === 0 ? (
            <Text type="secondary">Ingen labels tilknyttet.</Text>
          ) : (
            <Space size={8} wrap>
              {sortedLeadLabels.map((label) => {
                const tag = (
                  <Tag
                    color={label.color || undefined}
                    style={{ minWidth: 80, textAlign: 'center', fontWeight: 600 }}
                  >
                    {label.name}
                  </Tag>
                );

                return (
                  <Space key={label.id} size={4} align="center">
                    {label.hideInSearch ? (
                      <Tooltip title="Skjuler leadet i virksomhedssøgning">{tag}</Tooltip>
                    ) : (
                      tag
                    )}
                    {canManage && (
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => handleDetachLabel(label)}
                        loading={removingLabelId === label.id}
                      >
                        Fjern
                      </Button>
                    )}
                  </Space>
                );
              })}
            </Space>
          )}
          {canManage && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              {allLabels.length === 0 && !labelsLoading ? (
                <Text type="secondary">
                  Der er ingen labels endnu. Opret dem under Tilpasning.
                </Text>
              ) : (
                <Space size="middle" wrap>
                  <Select<string[]>
                    mode="multiple"
                    placeholder="Vælg labels"
                    showSearch
                    optionFilterProp="label"
                    style={{ minWidth: 220 }}
                    value={selectedLabelIds}
                    onChange={setSelectedLabelIds}
                    options={labelOptions}
                    loading={labelsLoading}
                    disabled={labelsLoading || labelOptions.length === 0}
                    allowClear
                  />
                  <Button
                    type="primary"
                    onClick={handleAttachLabel}
                    loading={attachLoading}
                    disabled={selectedLabelIds.length === 0 || labelsLoading}
                  >
                    Tilknyt labels
                  </Button>
                </Space>
              )}
              {!labelsLoading && labelOptions.length === 0 && allLabels.length > 0 && (
                <Text type="secondary">Alle tilgængelige labels er allerede tilknyttet.</Text>
              )}
            </>
          )}
        </Space>
      </Card>
              </Space>
            )
          },
          {
            key: 'activity',
            label: 'Aktivitet',
            children: (
              <Card title="Historik" bodyStyle={{ padding: 0 }}>
            <List
              dataSource={events}
              locale={{ emptyText: 'Ingen historik endnu' }}
              renderItem={(event) => (
                <List.Item key={event.id}>
                  <List.Item.Meta
                    title={
                      <Space direction="vertical" size={0}>
                        <Text strong>{leadEventTypeLabels[event.event_type] || event.event_type}</Text>
                        <Text type="secondary">{formatDateTime(event.created_at)}</Text>
                      </Space>
                    }
                    description={(() => {
                      const items: ReactNode[] = [];
                      const isStatusChange = event.event_type === 'STATUS_CHANGED';
                      const isMeetingEvent = event.event_type.startsWith('MEETING_');

                      const appendParagraphs = (text: string, keyPrefix: string) => {
                        const normalized = text.replace(/\s·\s/g, '\n');
                        const lines = normalized.split(/\n+/).map((line) => line.trim());
                        lines
                          .filter((line) => Boolean(line))
                          .forEach((line, index) => {
                            items.push(
                              <Paragraph key={`${keyPrefix}-${index}`} style={{ marginBottom: 0 }}>
                                {line}
                              </Paragraph>
                            );
                          });
                      };

                      if (isStatusChange && (event.from_status || event.to_status)) {
                        items.push(
                          <Text key="status-change">
                            {statusLabelMap.get(event.from_status || '') || event.from_status || '—'} →{' '}
                            {statusLabelMap.get(event.to_status || '') || event.to_status || '—'}
                          </Text>
                        );
                      }

                      if (isMeetingEvent) {
                        const note = event.notes;
                        if (note) {
                          appendParagraphs(note, 'meeting-note');
                        } else {
                          items.push(
                            <Text key="meeting-empty" type="secondary">
                              Ingen mødedetaljer
                            </Text>
                          );
                        }
                      } else if (event.notes) {
                        appendParagraphs(event.notes, 'event-note');
                      }

                      if (items.length === 0) {
                        return undefined;
                      }

                      return (
                        <Space direction="vertical" size={2}>
                          {items}
                        </Space>
                      );
                    })()}
                  />
                </List.Item>
              )}
            />
          </Card>
            )
          },
          {
            key: 'data',
            label: <Badge count={urgentPropertyAlerts.length} offset={[8, 0]}><span>Data</span></Badge>,
            children: (
              <>

      <Card loading={loading} title="CVR-oplysninger">
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {cvrLookupLoading && <Text type="secondary">Henter CVR-oplysninger...</Text>}
          {cvrLookupError && !cvrLookupLoading && <Text type="danger">{cvrLookupError}</Text>}
          {lead?.cvrnummer ? (
            cvrSummary ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Virksomhedsnavn">
                  {cvrSummary.navn || lead.company_name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {cvrSummary.virksomhed_status || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Branche">
                  {cvrSummary.branchekode || cvrSummary.branchetekst
                    ? [cvrSummary.branchekode, cvrSummary.branchetekst]
                        .filter(Boolean)
                        .join(' - ')
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefon">
                  {cvrSummary.telefon || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="E-mail">
                  {cvrSummary.email || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Adresse">
                  {cvrAddress || '—'}
                </Descriptions.Item>
                {cvrSummary.co_navn && (
                  <Descriptions.Item label="C/O">
                    {cvrSummary.co_navn}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Adresseanvendelse">
                  {cvrSummary.adresseringanvendelse || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Sidst opdateret">
                  {formatDateTime(cvrSummary.opdateret_tidspunkt)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text>
                Ingen CVR-data fundet for {formattedCvr || lead.cvrnummer}.
              </Text>
            )
          ) : (
            <Text type="secondary">
              Tilføj et CVR-nummer for at hente oplysninger fra Datafordeler.
            </Text>
          )}
        </Space>
      </Card>
      <Card loading={loading} title="Ejendomsportefølje">
        {!lead?.cvrnummer ? (
          <Text type="secondary">Tilføj et CVR-nummer for at hente ejendomsdata.</Text>
        ) : (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {propertyPortfolioLoading ? (
                <Spin size="small" />
              ) : propertyPortfolioError ? (
                <Alert type="warning" showIcon message={propertyPortfolioError} />
              ) : propertyPortfolio ? (
                <>
                  <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                    <Descriptions.Item label="Bygninger">{propertyPortfolio.buildingCount}</Descriptions.Item>
                    <Descriptions.Item label="Samlet areal">{formatPortfolioArea(propertyPortfolio.totalBuildingArea)}</Descriptions.Item>
                    <Descriptions.Item label="Boligareal">{formatPortfolioArea(propertyPortfolio.totalResidentialArea)}</Descriptions.Item>
                    <Descriptions.Item label="Erhvervsareal">{formatPortfolioArea(propertyPortfolio.totalCommercialArea)}</Descriptions.Item>
                  </Descriptions>
                  {propertyPortfolio.properties.length ? (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text strong>Ejede ejendomme</Text>
                      <Table
                        size="small"
                        rowKey={(property) => property.bfeNumber || `unknown-${property.totalBuildingArea}-${property.buildingCount}`}
                        dataSource={propertyPortfolio.properties}
                        pagination={propertyPortfolio.properties.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
                        expandable={{
                          expandRowByClick: true,
                          rowExpandable: (property) => property.buildings.length > 0,
                          expandedRowRender: (property) => (
                            <Table
                              size="small"
                              rowKey="id"
                              dataSource={property.buildings}
                              pagination={false}
                              columns={[
                                { title: 'Bygningsnummer', dataIndex: 'buildingNumber', render: (value: number | null) => value ?? 'Ikke oplyst' },
                                { title: 'Anvendelse', dataIndex: 'usage', render: (value: string | null) => value || 'Ikke oplyst' },
                                { title: 'Samlet areal', dataIndex: 'totalArea', render: (value: number | null) => value === null ? 'Ikke oplyst' : formatPortfolioArea(value) },
                                { title: 'Boligareal', dataIndex: 'residentialArea', render: (value: number | null) => value === null ? 'Ikke oplyst' : formatPortfolioArea(value) },
                                { title: 'Erhvervsareal', dataIndex: 'commercialArea', render: (value: number | null) => value === null ? 'Ikke oplyst' : formatPortfolioArea(value) },
                                { title: 'Opført', dataIndex: 'buildYear', render: (value: number | null) => value ?? 'Ikke oplyst' }
                              ]}
                            />
                          )
                        }}
                        columns={[
                          {
                            title: 'BFE-nummer',
                            dataIndex: 'bfeNumber',
                            render: (value: string | null) => value || 'Ikke oplyst'
                          },
                          { title: 'Alle bygninger', dataIndex: 'buildingCount' },
                          { title: 'Relevante bygninger', dataIndex: 'relevantBuildingCount' },
                          {
                            title: 'Relevant areal',
                            dataIndex: 'relevantBuildingArea',
                            render: (value: number) => formatPortfolioArea(value)
                          },
                          {
                            title: 'Samlet areal',
                            dataIndex: 'totalBuildingArea',
                            render: (value: number) => formatPortfolioArea(value)
                          },
                          {
                            title: 'Anvendelser',
                            dataIndex: 'usages',
                            render: (usages: string[]) => usages.length ? usages.join(', ') : 'Ikke oplyst'
                          }
                        ]}
                      />
                    </Space>
                  ) : null}
                </>
              ) : (
                <Text type="secondary">Virksomheden er ikke registreret som bygningsejer.</Text>
              )}
            </Space>
            <Divider style={{ margin: 0 }} />
            <Text strong>Virksomhedens CVR-adresse</Text>
            {propertyDetailLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : propertyDetailError ? (
          <Alert type="warning" showIcon message={propertyDetailError} />
        ) : propertyDetail ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="DAR-adresser">{propertyDetail.darAddresses.length}</Descriptions.Item>
              <Descriptions.Item label="BBR-objekter">{propertyDetail.bbrObjects.length}</Descriptions.Item>
              <Descriptions.Item label="Seneste BBR-opdatering" span={2}>{formatDateTime(propertyDetail.latestSourceUpdatedAt)}</Descriptions.Item>
            </Descriptions>
            <Collapse
              items={[
                {
                  key: 'addresses',
                  label: `DAR-adresser (${propertyDetail.darAddresses.length})`,
                  children: propertyDetail.darAddresses.length ? (
                    <List
                      bordered
                      dataSource={propertyDetail.darAddresses}
                      renderItem={(address) => (
                        <List.Item>
                          <Space direction="vertical" size={4}>
                            <Text strong>{address.displayAddress || 'Adresse uden visningsnavn'}</Text>
                            <Space size={6} wrap>
                              {address.status ? <Tag>{address.status === '3' ? 'Gældende' : `Status ${address.status}`}</Tag> : null}
                              {address.streetName ? <Tag>{address.streetName}</Tag> : null}
                              {address.postalCode || address.city ? <Tag>{[address.postalCode, address.city].filter(Boolean).join(' ')}</Tag> : null}
                            </Space>
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : <Empty description="Ingen DAR-adresser fundet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                },
                {
                  key: 'buildings',
                  label: `BBR-objekter på CVR-adressen (${propertyDetail.bbrObjects.length}, ikke nødvendigvis ejet af virksomheden)`,
                  children: propertyDetail.bbrObjects.length ? (
                    <List
                      dataSource={propertyDetail.bbrObjects}
                      renderItem={(item) => (
                        <List.Item>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>{item.buildingNumber ? `Bygning ${item.buildingNumber}` : item.type || 'BBR-objekt'}</Text>
                            <Text type="secondary">{item.displayAddress || 'Adresse ikke angivet'}</Text>
                            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
                              <Descriptions.Item label="Status">{item.statusLabel || 'Ikke oplyst'}</Descriptions.Item>
                              <Descriptions.Item label="Anvendelse">{item.usage || 'Ikke oplyst'}</Descriptions.Item>
                              {item.totalArea !== null ? <Descriptions.Item label="Samlet areal">{formatPortfolioArea(item.totalArea)}</Descriptions.Item> : null}
                              {item.commercialArea !== null ? <Descriptions.Item label="Erhvervsareal">{formatPortfolioArea(item.commercialArea)}</Descriptions.Item> : null}
                              {item.residentialArea !== null ? <Descriptions.Item label="Boligareal">{formatPortfolioArea(item.residentialArea)}</Descriptions.Item> : null}
                              {item.buildYear !== null ? <Descriptions.Item label="Opført">{item.buildYear}</Descriptions.Item> : null}
                              {item.rebuildYear !== null ? <Descriptions.Item label="Ombygget">{item.rebuildYear}</Descriptions.Item> : null}
                              {item.floors !== null ? <Descriptions.Item label="Etager">{item.floors}</Descriptions.Item> : null}
                              {item.roofMaterial ? <Descriptions.Item label="Tag">{item.roofMaterial}</Descriptions.Item> : null}
                              {item.outerWallMaterial ? <Descriptions.Item label="Ydervæg">{item.outerWallMaterial}</Descriptions.Item> : null}
                              {item.heatingFuel ? <Descriptions.Item label="Varme">{item.heatingFuel}</Descriptions.Item> : null}
                              {item.asbestosMaterial ? <Descriptions.Item label="Asbest">{item.asbestosMaterial}</Descriptions.Item> : null}
                            </Descriptions>
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : <Empty description="Ingen BBR-objekter fundet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                },
                {
                  key: 'cases',
                  label: `Byggesager (${propertyDetail.buildingCases?.length || 0})`,
                  children: propertyDetail.buildingCases?.length ? (
                    <List
                      dataSource={propertyDetail.buildingCases}
                      renderItem={(item) => (
                        <List.Item>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Space size={6}>
                              <Text strong>{item.caseCodeLabel || item.caseType || 'Byggesag'}</Text>
                              <Tooltip title={item.caseCodeLabel?.toLowerCase().includes('uden ibrugtagningstilladelse')
                                ? 'Der er registreret byggetilladelse, men ingen efterfølgende ibrugtagningstilladelse. Sagen kan være uafsluttet, ændret eller mangelfuldt registreret.'
                                : 'Der er registreret byggetilladelse og en efterfølgende godkendelse til at tage byggeriet eller ændringen i brug.'}>
                                <InfoCircleOutlined style={{ color: '#1677ff' }} />
                              </Tooltip>
                            </Space>
                            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
                              <Descriptions.Item label="Sagsnummer">{item.caseNumber || 'Ikke oplyst'}</Descriptions.Item>
                              <Descriptions.Item label="Adresse">{item.address || 'Ikke oplyst'}</Descriptions.Item>
                              <Descriptions.Item label="Bygningstype">{item.buildingUsage || 'Ikke oplyst'}</Descriptions.Item>
                              <Descriptions.Item label="Påbegyndt">{item.startDate ? formatDateTime(item.startDate) : 'Ikke oplyst'}</Descriptions.Item>
                              <Descriptions.Item label="Fuldført">{item.completionDate ? formatDateTime(item.completionDate) : 'Ikke oplyst'}</Descriptions.Item>
                            </Descriptions>
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : <Empty description="Ingen byggesager fundet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                },
                {
                  key: 'signals',
                  label: `Forsikringssignaler (${insuranceEvents?.events?.length || 0})`,
                  children: insuranceEventsError ? <Alert type="warning" showIcon message={insuranceEventsError} /> : insuranceEvents?.events?.length ? (
                    <List
                      bordered
                      dataSource={insuranceEvents.events}
                      renderItem={(event) => (
                        <List.Item>
                          <Space direction="vertical" size={4}>
                            <Space size={6} wrap><Tag color={event.severity === 'CRITICAL' ? 'red' : event.severity === 'HIGH' ? 'orange' : 'blue'}>{event.severity}</Tag><Text strong>{event.title}</Text></Space>
                            {event.description ? <Text>{event.description}</Text> : null}
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : <Empty description="Ingen forsikringssignaler registreret" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                },
                {
                  key: 'changes',
                  label: `Registerændringer (${propertyChanges?.events?.length || 0})`,
                  children: propertyChangesError ? <Alert type="warning" showIcon message={propertyChangesError} /> : propertyChanges?.events?.length ? (
                    <List
                      bordered
                      dataSource={propertyChanges.events}
                      renderItem={(event) => (
                        <List.Item>
                          <Space direction="vertical" size={4}>
                            <Space size={6} wrap><Tag>{event.registerType}</Tag><Tag>{event.changeType}</Tag><Text strong>{event.objectId}</Text></Space>
                            <Text type="secondary">Registreret {formatDateTime(event.detectedAt)} · {Object.keys(event.changedFields || {}).length} ændrede felter</Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : <Empty description="Ingen registerændringer registreret" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                }
              ]}
            />
          </Space>
        ) : (
          <Empty description="Ingen ejendomsdata fundet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
          </Space>
        )}
      </Card>
              </>
            )
          }
        ]}
      />

      <Drawer
        title={editingTodo ? 'Rediger opgave' : 'Ny opgave'}
        open={todoDrawerOpen}
        onClose={handleTodoDrawerClose}
        width={480}
        destroyOnClose
      >
        <Form form={todoForm} layout="vertical" onFinish={handleTodoFormSubmit}>
          <Form.Item
            name="title"
            label="Titel"
            rules={[{ required: true, message: 'Titel skal angives' }]}
          >
            <Input placeholder="Beskriv opgaven" maxLength={160} />
          </Form.Item>
          <Form.Item name="description" label="Beskrivelse">
            <Input.TextArea rows={3} placeholder="Uddybning" allowClear />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Vælg en status' }]}
              >
                <Select
                  options={Object.entries(todoStatusLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="priority"
                label="Prioritet"
                rules={[{ required: true, message: 'Vælg en prioritet' }]}
              >
                <Select
                  options={Object.entries(todoPriorityLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="due_at" label="Forfaldsdato">
                <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="assigned_to" label="Ansvarlig">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Vælg bruger"
                  loading={assigneesLoading}
                  options={assignees.map((user) => ({
                    value: user.id,
                    label: user.full_name?.trim() || user.email || 'Ukendt bruger'
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="sync_to_calendar"
            label="Synkroniser til kalender"
            valuePropName="checked"
            extra="Opretter og opdaterer en kalenderaftale på forfaldstidspunktet"
          >
            <Switch disabled={!watchedTodoDue} />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleTodoDrawerClose}>Annuller</Button>
            <Button type="primary" htmlType="submit" loading={savingTodo}>
              {editingTodo ? 'Opdater opgave' : 'Opret opgave'}
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer
        title={meetingDrawerTitle}
        open={meetingDrawerOpen}
        onClose={handleMeetingDrawerClose}
        width={420}
        destroyOnClose
      >
        <Form form={meetingForm} layout="vertical" onFinish={handleMeetingSubmit}>
          <Form.Item name="scheduled_for" label="Dato og tid">
            <Input type="datetime-local" />
          </Form.Item>
          <Form.Item name="location" label="Lokation">
            <Input placeholder="Lokation" allowClear />
          </Form.Item>
          <Form.Item name="summary" label="Beskrivelse">
            <Input placeholder="Kort beskrivelse" allowClear />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleMeetingDrawerClose}>Annuller</Button>
            <Button type="primary" htmlType="submit" loading={savingMeeting}>
              {meetingDrawerSubmitLabel}
            </Button>
          </Space>
        </Form>
      </Drawer>

      <Modal
        title={
          policyInsuranceModalPolicyId
            ? `Tilknyt forsikringstyper: ${
                policyDocuments.find((doc) => doc.id === policyInsuranceModalPolicyId)?.file_name || 'Police'
              }`
            : 'Tilknyt forsikringstyper'
        }
        open={policyInsuranceModalOpen}
        onCancel={handleClosePolicyInsuranceModal}
        onOk={handleSavePolicyInsuranceTypes}
        okText="Gem"
        confirmLoading={policyInsuranceModalSaving}
        destroyOnClose
        width={640}
      >
        <List
          dataSource={availableInsuranceTypes}
          loading={insuranceTypesLoading}
          locale={{ emptyText: 'Ingen forsikringstyper oprettet endnu' }}
          renderItem={(item) => (
            <List.Item key={item.id}>
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <Checkbox
                      checked={selectedPolicyInsuranceTypeIds.includes(item.id)}
                      onChange={(e) => handleTogglePolicyInsuranceType(item.id, e.target.checked)}
                    >
                      {item.title}
                    </Checkbox>
                    {item.category ? <Tag>{item.category}</Tag> : null}
                    {item.mandatory ? <Tag color="red">Lovpligtig</Tag> : null}
                  </Space>
                }
                description={
                  item.description ? (
                    <Text>{item.description}</Text>
                  ) : (
                    <Text type="secondary">Ingen beskrivelse</Text>
                  )
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title={isLinkingExistingGroup ? 'Tilføj lead til samling' : 'Saml leads'}
        open={linkModalOpen}
        onCancel={closeLinkModal}
        onOk={handleLinkSubmit}
        okText={isLinkingExistingGroup ? 'Tilføj lead' : 'Opret samling'}
        okButtonProps={{ disabled: !selectedLinkLeadId }}
        confirmLoading={linkModalSubmitting}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Select
            showSearch
            placeholder="Søg efter lead via navn eller CVR"
            value={selectedLinkLeadId ?? undefined}
            onSearch={handleLeadSearch}
            onChange={(value) => setSelectedLinkLeadId(value ?? null)}
            options={leadSearchResults.map((result) => ({
              value: result.id,
              label: `${result.company_name || 'Lead'}${result.cvrnummer ? ` · CVR ${formatCvrNumber(result.cvrnummer)}` : ''}`
            }))}
            loading={leadSearchLoading}
            filterOption={false}
            showArrow={false}
            allowClear
            style={{ width: '100%' }}
            notFoundContent={leadSearchEmptyText}
          />
          <Text type="secondary">
            {isLinkingExistingGroup
              ? 'Søg efter eksisterende leads og tilføj dem til denne samling.'
              : 'Søg efter eksisterende leads og link dem sammen.'}
          </Text>
        </Space>
      </Modal>

      <Modal
        title={historyTodo ? `Historik · ${historyTodo.title}` : 'Opgavehistorik'}
        open={todoHistoryModalOpen}
        onCancel={handleTodoHistoryClose}
        footer={null}
        width={560}
        destroyOnClose
      >
        <List
          loading={todoHistoryLoading}
          dataSource={todoHistory}
          locale={{ emptyText: 'Ingen historik endnu' }}
          renderItem={(event) => (
            <List.Item key={event.id}>
              <List.Item.Meta
                title={
                  <Space direction="vertical" size={2}>
                    <Text strong>{event.description || 'Hændelse'}</Text>
                    <Space size={8} wrap>
                      <Tag color="blue">{event.event_type}</Tag>
                      <Text type="secondary">{formatDateTimeFull(event.created_at)}</Text>
                      {event.actor_full_name ? (
                        <Text type="secondary">{event.actor_full_name}</Text>
                      ) : (
                        <Text type="secondary">System</Text>
                      )}
                    </Space>
                  </Space>
                }
                description={null}
              />
            </List.Item>
          )}
        />
      </Modal>

    </Space>
  );
}
